// [SCOPE 051 demo / 053] Authored it-IT bundle.
//
// Demonstrates the localization framework end-to-end: the keys translated here
// render in Italian when the locale is it-IT; any key NOT present falls back to
// en-US per key (FR-002). Currently scoped to the landing page (the wired slice)
// plus common/settings; the full app-wide extraction is spec 052.
//
// Methodology/lifecycle terms and brand/compliance names (wxKanban, SOC 2, HIPAA,
// GDPR, HITRUST) are intentionally left untranslated.
import type { PartialLocaleBundle } from "./en-US";

export const itIT: PartialLocaleBundle = {
  common: {
    save: "Salva",
    cancel: "Annulla",
    close: "Chiudi",
    delete: "Elimina",
    edit: "Modifica",
    loading: "Caricamento…",
    saving: "Salvataggio…",
    saved: "Salvato",
    error: "Si è verificato un errore",
    retry: "Riprova",
    yes: "Sì",
    no: "No",
  },
  settings: {
    language: {
      title: "Lingua",
      description: "Scegli la lingua dell'interfaccia.",
      help: "Le lingue non tradotte ricadono sull'inglese finché i loro testi non vengono redatti.",
      saved: "Lingua aggiornata.",
    },
  },
  landing: {
    nav: {
      home: "Home",
      features: "Funzionalità",
      pricing: "Prezzi",
      docs: "Aiuto/Tutorial",
      signIn: "Accedi",
    },
    hero: {
      subtitle: "Sistema di gestione progetti con IA",
      description:
        "wxKanban è un sistema completo di gestione progetti sviluppato attorno all'IA, con un kit di partenza pronto per la produzione e adatto a team di qualsiasi dimensione. Progettato per sicurezza, conformità e integrazioni senza interruzioni.",
      getStarted: "Inizia ora",
      learnMore: "Scopri di più",
    },
    install: {
      eyebrow: "Pronto in ~30 minuti",
      heading: "Installazione e configurazione",
      description:
        "Nuovo su wxKanban? La nostra guida passo passo ti porta da un computer nuovo a una configurazione funzionante in VS Code: installazione di Claude Code, aggiunta del Dev Cockpit e avvio del tuo primo progetto. Nessuna esperienza precedente richiesta.",
      step1: {
        title: "Installare gli strumenti",
        body: "VS Code, Node.js e Claude Code, con comandi da copiare e incollare.",
      },
      step2: {
        title: "Aggiungere il Dev Cockpit",
        body: "Installa l'estensione e collegala al tuo progetto wxKanban.",
      },
      step3: {
        title: "Avviare il tuo primo workflow",
        body: "Scope, spec e build, guidati attraverso il ciclo di vita a 6 fasi.",
      },
      cta: "Vedi le istruzioni di configurazione",
    },
    features: {
      heading: "Funzionalità potenti",
      subheading:
        "Tutto ciò che serve per gestire i progetti, tracciare il tempo e collaborare con il tuo team",
      aiPowered: {
        title: "Basato sull'IA",
        description:
          "Suggerimenti intelligenti sulle attività e ottimizzazione dei flussi di lavoro, basati sul tuo strumento di IA preferito per lo sviluppo",
      },
      security: {
        title: "Sicurezza per le aziende",
        description: "Conforme a SOC 2, HIPAA e GDPR, con crittografia AES-256-GCM",
      },
      integration: {
        title: "Integrazione multisistema",
        description:
          "Collega Jira, Monday.com, Asana, Trello e altre piattaforme di gestione progetti senza problemi",
      },
      conversion: {
        title: "Conversione di applicazioni legacy",
        intro: "Converti le tue applicazioni legacy come:",
        more: "e altro ancora",
      },
      collaboration: {
        title: "Collaborazione di team",
        item1:
          "Gestione del team in tempo reale con controllo degli accessi basato sui ruoli",
        item2:
          "Aggiungi i documenti SDLC della tua azienda per analizzarli ai fini della conformità",
        item3:
          "Assegna l'SDLC a ciascun membro del team per rispettare i controlli di audit",
        item4:
          "Lascia che l'IA ti aiuti a rispettare tutti i requisiti di audit",
      },
      lifecycle: {
        title: "Ciclo di vita a 6 fasi",
        item1: "Metodologia di qualità strutturata, da Design a Release",
        item2:
          "Comandi open source e Claude per gestire l'intero processo di sviluppo",
        item3:
          "Aggiorna automaticamente le specs e le attività sul tuo sistema di gestione progetti",
        item4: "Flussi automatici per garantire la conformità agli audit",
        item5: "Rapporto di audit completo a ogni rilascio",
        footer:
          "Ciò che scrivi e generi in VS Code, ora in un vero sistema di gestione progetti",
      },
    },
    pricing: {
      heading: "Prezzi semplici e trasparenti",
      subheading:
        "Scegli il piano perfetto per il tuo team. Sempre con la flessibilità di passare a un livello superiore o inferiore",
      mostPopular: "IL PIÙ POPOLARE",
      perMonth: "/mese",
      free: "Gratis",
      faqText: "Hai domande sui prezzi?",
      contactSales: "Contatta il nostro team commerciale",
      tiers: {
        single: {
          name: "Utente singolo",
          userLimit: "1 utente",
          f1: "Progetti illimitati",
          f2: "Una integrazione di gestione progetti",
          f3: "Supporto via e-mail",
          cta: "Inizia ora",
        },
        smb: {
          name: "Piccola/Media impresa",
          userLimit: "Fino a 5 utenti",
          f1: "Certificato SOC 2 e HIPAA",
          f2: "Una sola azienda",
          f3: "Gestione del team",
          f4: "Supporto via e-mail",
          cta: "Inizia ora",
        },
        consultant: {
          name: "Consulente",
          userLimit: "Team illimitato",
          f1: "Membri del team illimitati",
          f2: "Più connessioni di gestione progetti",
          f3: "Tracciamento del tempo avanzato",
          f4: "Generazione di fatture",
          f5: "Flussi di lavoro personalizzati",
          f6: "Accesso alle API",
          cta: "Registrati",
        },
        enterprise: {
          name: "Enterprise",
          priceUnit: "+ 5 $/utente oltre i 25",
          userLimit: "25 o più utenti",
          f1: "25 utenti inclusi",
          f2: "5 $/mese per utente attivo oltre i 25",
          f3: "Tutto illimitato",
          f4: "Supporto dedicato",
          f5: "Integrazioni personalizzate",
          f6: "Garanzia di SLA",
          cta: "Inizia ora",
        },
      },
    },
    compliance: {
      heading: "Sicurezza di livello enterprise",
      subheading:
        "Progettato con la conformità e la sicurezza come requisiti fondamentali",
      soc2: {
        name: "Allineato a SOC 2",
        description: "Progettato secondo i Trust Services Criteria di SOC 2",
      },
      hipaa: {
        name: "Controlli orientati a HIPAA",
        description:
          "Misure di salvaguardia basate sugli standard per le informazioni sanitarie protette",
      },
      gdpr: {
        name: "Pronto per il GDPR",
        description: "Controlli di privacy e protezione dei dati integrati",
      },
      encryption: {
        title: "Crittografia a riposo e in transito",
        description: "Crittografia AES-256-GCM per tutti i dati",
      },
      auth: {
        title: "Autenticazione sicura",
        description:
          "Hashing bcrypt con token JWT e rotazione dei token di aggiornamento",
      },
      audit: {
        title: "Registrazione di audit",
        description: "Traccia di audit completa per la conformità e la trasparenza",
      },
      rbac: {
        title: "Controllo degli accessi basato sui ruoli",
        description: "Autorizzazioni granulari per team e dati",
      },
      trustLead: "Progettato per la fiducia",
      trustBody:
        "La sicurezza dei tuoi dati è la nostra massima priorità. Ci atteniamo a standard di sicurezza riconosciuti e verifichiamo regolarmente i nostri controlli.",
    },
    cta: {
      headline: "Pronto a trasformare la tua gestione progetti?",
      subhead: "Unisciti a migliaia di team che usano wxKanban per ottimizzare i propri flussi di lavoro",
      getStarted: "Inizia ora",
      viewPricing: "Vedi i prezzi",
    },
    footer: {
      tagline: "Gestione progetti basata sull'IA per i team moderni",
      product: "Prodotto",
      company: "Azienda",
      legal: "Note legali",
      features: "Funzionalità",
      pricing: "Prezzi",
      documentation: "Documentazione",
      about: "Chi siamo",
      blog: "Blog",
      contact: "Contatti",
      bio: "Biografia",
      privacy: "Privacy",
      terms: "Termini",
      security: "Sicurezza",
      rights: "© 2026 wxKanban. Tutti i diritti riservati.",
    },
  },
  login: {
    title: "Bentornato",
    subtitle: "Accedi al tuo account wxKanban",
    email: "Email",
    emailPlaceholder: "tu@azienda.com",
    password: "Password",
    rememberMe: "Ricordami",
    forgotPassword: "Password dimenticata?",
    signIn: "Accedi",
    signingIn: "Accesso in corso…",
    genericError: "Si è verificato un errore imprevisto. Riprova.",
    noAccount: "Non hai un account?",
    register: "Registra la tua azienda",
    tagline: "Gestione attività multipiattaforma",
    emailLabel: "Indirizzo email",
    showPasswordAria: "Tieni premuto per mostrare la password",
    registerLink: "Registrati qui",
    connectPmPrompt: "Oppure collega un sistema di project management per iniziare:",
    loginFailed: "Accesso non riuscito",
  },
  forgotPassword: {
    title: "Password dimenticata",
    description: "Inserisci la tua e-mail e ti invieremo un link per reimpostare la password.",
    successMessage: "Se esiste un account con questa e-mail, è stato inviato un link per reimpostare la password. Controlla la tua casella di posta.",
    backToSignIn: "Torna all'accesso",
    emailLabel: "Indirizzo e-mail",
    emailPlaceholder: "tu@esempio.com",
    submit: "Invia link di reimpostazione",
    submitting: "Invio...",
    errorFailed: "Impossibile inviare il link di reimpostazione",
  },
  resetPassword: {
    title: "Reimposta password",
    subtitle: "Inserisci la tua nuova password qui sotto.",
    newPassword: "Nuova password",
    confirmNewPassword: "Conferma nuova password",
    updatePassword: "Aggiorna password",
    updating: "Aggiornamento...",
    successMessage: "La tua password è stata reimpostata correttamente. Reindirizzamento all'accesso...",
    backToSignIn: "Torna all'accesso",
    errMissingLink: "Questo link per reimpostare la password non è valido o è incompleto.",
    errMissingToken: "Il token di reimpostazione è mancante. Utilizza il link dalla tua e-mail.",
    errPasswordMin: "La password deve contenere almeno 8 caratteri",
    errPasswordMismatch: "Le password non corrispondono",
    errFailed: "Impossibile reimpostare la password",
  },
  changePassword: {
    title: "Cambia la tua password",
    welcome: "Benvenuto, {{name}}! Per motivi di sicurezza, cambia la tua password temporanea prima di continuare.",
    currentLabel: "Password attuale (temporanea)",
    currentPlaceholder: "Inserisci la tua password temporanea",
    newLabel: "Nuova password",
    newPlaceholder: "Crea una nuova password (min. 8 caratteri)",
    confirmLabel: "Conferma nuova password",
    confirmPlaceholder: "Conferma la tua nuova password",
    submit: "Cambia password e continua",
    needHelp: "Hai bisogno di aiuto? Contatta il tuo amministratore o il",
    supportTeam: "team di supporto",
    errMinLength: "La nuova password deve contenere almeno 8 caratteri",
    errMismatch: "Le password non corrispondono",
    errSameAsOld: "La nuova password deve essere diversa da quella attuale",
    errFailed: "Impossibile cambiare la password",
    successRedirect: "Password cambiata con successo! Reindirizzamento alla dashboard...",
  },
  appnav: {
    dashboard: "Dashboard",
    teamMembers: "Membri del team",
    projects: "Progetti",
    pmIntegrations: "Integrazioni PM",
    myTasks: "Le mie attività",
    workHub: "Area di lavoro",
    timeBilling: "Tempo e fatturazione",
    invoices: "Fatture",
    reports: "Report",
    settings: "Impostazioni",
    gettingStarted: "Guida introduttiva",
    manual: "Manuale", // [SCOPE 141 / T016]
    help: "Aiuto",
    signOut: "Esci",
  },
  register: {
    next: "Avanti",
    loading: "Caricamento...",
    back: "Indietro",
    companyTitle: "Informazioni sull'azienda",
    companySubtitle: "Parlaci della tua azienda così possiamo configurare la fatturazione e le comunicazioni",
    companyNameLabel: "Nome dell'azienda *",
    billingEmailLabel: "E-mail di fatturazione *",
    billingAddressLabel: "Indirizzo di fatturazione *",
    countryLabel: "Paese *",
    phoneLabel: "Numero di telefono *",
    referredByLabel: "Segnalato da (facoltativo)",
    referredByHint: "Se un distributore ti ha segnalato, inserisci il suo codice.",
    errCompanyNameRequired: "Il nome dell'azienda è obbligatorio",
    errCompanyNameMin: "Il nome dell'azienda deve contenere almeno 2 caratteri",
    errCompanyNameMax: "Il nome dell'azienda deve contenere meno di 255 caratteri",
    errBillingEmailRequired: "L'e-mail di fatturazione è obbligatoria",
    errEmailInvalid: "Inserisci un indirizzo e-mail valido",
    errBillingAddressRequired: "L'indirizzo di fatturazione è obbligatorio",
    errBillingAddressMin: "L'indirizzo di fatturazione deve contenere almeno 10 caratteri",
    errBillingAddressMax: "L'indirizzo di fatturazione deve contenere meno di 500 caratteri",
    errCountryRequired: "Il paese è obbligatorio",
    errCountryInvalid: "Seleziona un codice paese valido",
    errPhoneRequired: "Il numero di telefono è obbligatorio",
    errPhoneMin: "Il numero di telefono deve contenere almeno 10 caratteri",
    errPhoneMax: "Il numero di telefono deve contenere meno di 20 caratteri",
    adminTitle: "Crea account amministratore",
    adminSubtitle: "Configura il tuo account amministratore per accedere a wxKanban. È il primo utente che verrà creato per la tua azienda.",
    emailLabel: "Indirizzo e-mail *",
    firstNameLabel: "Nome *",
    lastNameLabel: "Cognome (facoltativo)",
    passwordLabel: "Password *",
    pwReqTitle: "Requisiti della password:",
    pwReqLength: "Almeno 12 caratteri",
    pwReqUpper: "Almeno una lettera maiuscola",
    pwReqLower: "Almeno una lettera minuscola",
    pwReqNumber: "Almeno un numero",
    pwReqSpecial: "Almeno un carattere speciale (!@#$%^&*)",
    termsPrefix: "Creando un account, accetti i nostri",
    termsOfService: "Termini di servizio",
    and: "e",
    privacyPolicy: "Informativa sulla privacy",
    creatingAccount: "Creazione dell'account...",
    completeRegistration: "Completa la registrazione",
    errEmailRequired: "L'e-mail è obbligatoria",
    errPasswordRequired: "La password è obbligatoria",
    errPasswordRequirements: "La password non soddisfa tutti i requisiti",
    errFirstNameRequired: "Il nome è obbligatorio",
    errFirstNameLength: "Il nome deve contenere tra 1 e 100 caratteri",
    errLastNameLength: "Il cognome deve contenere tra 1 e 100 caratteri",
    paymentMethod: "Metodo di pagamento",
    preparingPayment: "Preparazione del pagamento sicuro…",
    initPaymentError: "Impossibile inizializzare il pagamento",
    subscribingTo: "Ti stai abbonando a",
    at: "a",
    perMonth: "/mese",
    stripeNotice: "I dati della carta sono gestiti direttamente da Stripe. Nessun dato sensibile transita sui nostri server.",
    paymentFailedCard: "Pagamento non riuscito. Controlla la tua carta e riprova.",
    paymentFailed: "Pagamento non riuscito",
    processing: "Elaborazione…",
    completePayment: "Completa il pagamento",
    startPromo: "Inizia il periodo gratuito",
    inviteChecking: "Verifica del tuo invito in corso…",
    inviteInvalid: "Questo link di invito non è valido. Potrebbe essere già stato usato, revocato o scaduto.",
    inviteFreeMonths: "Sei stato invitato: i tuoi primi {{days}} giorni con {{plan}} sono gratuiti.",
    inviteBoundTo: "Questo invito è per {{email}} — ti registrerai con quell'indirizzo.",
    promoFreeForDays: "I tuoi primi {{days}} giorni sono gratuiti.",
    promoFirstChargeOn: "Il tuo primo pagamento di {{amount}} sarà il {{date}}.",
    promoFirstChargeAfter: "Il tuo primo pagamento di {{amount}} sarà dopo {{days}} giorni.",
    promoCardNotChargedToday: "Oggi non verrà addebitato nulla sulla tua carta. La conserviamo affinché il tuo abbonamento prosegua automaticamente.",
    tierSingleUser: "Utente singolo",
    tierSmallBusiness: "Piccola/Media impresa",
    tierConsultant: "Consulente",
    tierEnterprise: "Enterprise",
    tierFree: "Gratuito",
    confTitle: "Conferma la tua registrazione",
    confSubtitle: "Verifica le tue informazioni prima di completare la registrazione",
    confCompanyDesc: "Dettagli sulla tua organizzazione",
    confCompanyName: "Nome dell'azienda",
    confBillingEmail: "E-mail di fatturazione",
    subscriptionPlan: "Piano di abbonamento",
    subscriptionPlanDesc: "Il livello e il prezzo selezionati",
    selectedPlan: "Piano selezionato",
    monthlyPrice: "Prezzo mensile",
    adminAccount: "Account amministratore",
    adminAccountDesc: "I tuoi dati di accesso amministratore",
    nameLabel: "Nome",
    emailShort: "E-mail",
    termsClickPrefix: "Facendo clic su «Completa la registrazione», accetti i nostri",
    title: "Benvenuto su wxKanban",
    settingUp: "Configurazione del tuo account",
    account: "",
    stepCompany: "Azienda",
    stepPayment: "Pagamento",
    stepAdmin: "Admin",
    successTitle: "Azienda creata con successo!",
    verificationSent: "Un'e-mail di verifica è stata inviata a",
    whatsNext: "Prossimi passi:",
    next1: "Controlla la tua e-mail e clicca sul link di verifica",
    next2: "Accedi al tuo nuovo account wxKanban",
    next3: "Completa la configurazione del team e inizia a gestire i progetti",
    goToLogin: "Vai al login",
    createAnother: "Crea un altro account",
    haveAccount: "Hai già un account?",
    signIn: "Accedi",
  },
  settingsPage: {
    title: "Impostazioni",
    subtitle: "Gestisci le impostazioni della tua azienda e del tuo account",
    tabCompany: "Azienda",
    tabProfile: "Profilo",
    tabNotifications: "Notifiche",
    tabSecurity: "Sicurezza",
    companyInfo: "Informazioni aziendali",
    companyInfoDesc: "Aggiorna i dettagli e le informazioni di fatturazione della tua azienda",
    profileInfo: "Informazioni del profilo",
    profileInfoDesc: "Aggiorna le tue informazioni personali e le preferenze",
    language: "Lingua",
    changePassword: "Cambia password",
    notificationPrefs: "Preferenze di notifica",
    securitySettings: "Impostazioni di sicurezza",
    saveChanges: "Salva modifiche",
    saveProfile: "Salva profilo",
  },
  dashboard: {
    failedToFetch: "Recupero non riuscito: {{status}}",
    failedToLoadPendingTasks: "Impossibile caricare le attività in sospeso",
    adminTitle: "Dashboard amministratore",
    lastUpdated: "Ultimo aggiornamento: {{time}}",
    welcome: "Benvenuto, {{email}}",
    panelCompany: "Azienda",
    totalUsers: "Utenti totali",
    activeUsers: "Utenti attivi",
    byRole: "Per ruolo:",
    panelProjects: "Progetti",
    totalProjects: "Progetti totali",
    active: "Attivi",
    panelTeamSummary: "Riepilogo del team",
    total: "Totale",
    panelFinancials: "Finanze",
    revenue: "Ricavi (questo mese)",
    invoices: "Fatture",
    outstanding: "In sospeso",
    panelActivity: "Attività",
    loadingActivities: "Caricamento attività…",
    noActivity: "Nessuna attività recente",
    panelPendingTasks: "Attività in sospeso",
    loading: "Caricamento…",
    noActiveProjects: "Nessun progetto attivo.",
    colProject: "Progetto",
    colOpen: "Aperte",
    colInProgress: "In corso",
  },
  projects: {
    title: "Progetti",
    noProjects: "Ancora nessun progetto",
    count: "{{count}} progetti",
    countOne: "1 progetto",
    showAll: "Mostra tutti",
    myProjects: "I miei progetti",
    newProject: "Nuovo progetto",
    searchPlaceholder: "Cerca progetti…",
    sort: "Ordina:",
    sortUpdated: "Aggiornato",
    sortCreated: "Creato",
    sortName: "Nome",
    loadError: "Impossibile caricare i progetti",
    noMatch: "Nessun progetto corrisponde ai filtri attuali",
    clearFilters: "Cancella filtri",
    createFirst: "Crea il tuo primo progetto",
  },
  tasks: {
    back: "Torna alla dashboard",
    title: "Le mie attività",
    lastSync: "Ultima sincronizzazione:",
    syncNow: "Sincronizza",
    syncing: "Sincronizzazione…",
    allSources: "Tutte le fonti",
    allPriorities: "Tutte le priorità",
    priorityCritical: "Critica",
    priorityHigh: "Alta",
    priorityMedium: "Media",
    priorityLow: "Bassa",
    allStatuses: "Tutti gli stati",
    statusOpen: "Aperta",
    statusInProgress: "In corso",
    statusCompleted: "Completata",
    statusOrphaned: "Orfana",
    sortUpdated: "Aggiornate di recente",
    sortDue: "Scadenza",
    sortPriority: "Priorità",
    sortTitle: "Titolo",
    loading: "Caricamento attività…",
    noTasks: "Ancora nessuna attività",
    connectHint1: "Collega un sistema PM da",
    integrations: "Integrazioni",
    connectHint2: "e clicca su Sincronizza per importare le attività a te assegnate.",
  },
  integrations: {
    title: "Integrazioni dei sistemi di gestione progetti",
    subtitle: "Collega i tuoi strumenti di gestione progetti a wxKanban",
    viewTasks: "Visualizza le mie attività →",
    connectedSuccess: "✓ Sistema di gestione progetti collegato con successo!",
    loading: "Caricamento delle integrazioni...",
    connectedSince: "Collegato dal {{date}}",
    notConnected: "Non collegato",
    active: "✓ Attivo",
    disconnect: "Disconnetti",
    connect: "Connetti",
    needHelp: "Hai bisogno di aiuto per la connessione?",
    helpText: "Ogni sistema di gestione progetti richiede credenziali OAuth. Consulta la documentazione per le istruzioni di configurazione.",
    confirmDisconnect: "Disconnettere {{system}}?",
  },
  invoices: {
    title: "Fatture",
    subtitle: "Genera e gestisci le fatture dei clienti a partire dalle ore fatturabili",
    generate: "Genera fattura",
    statTotal: "Totale fatture",
    statRevenue: "Fatturato totale",
    statSent: "Fatture inviate",
    modalTitle: "Genera nuova fattura",
    modalDesc: "Seleziona l'intervallo di date per le ore fatturabili",
    startDate: "Data di inizio",
    endDate: "Data di fine",
    generateConfirm: "Genera",
    history: "Cronologia fatture",
    emptyTitle: "Nessuna fattura generata finora",
    emptyHint: "Crea la tua prima fattura per iniziare",
    colNumber: "N. fattura",
    colDate: "Data",
    colBilledTo: "Fatturato a",
    colHours: "Ore",
    colAmount: "Importo",
    colStatus: "Stato",
    colActions: "Azioni",
    statusSent: "Inviata",
    statusDraft: "Bozza",
    na: "N/D",
    detailTitle: "Fattura {{number}}",
    generatedOn: "Generata il {{date}}",
    hoursWorked: "Ore lavorate",
    totalAmount: "Importo totale",
    downloadPdf: "Scarica PDF",
    sendInvoice: "Invia fattura",
    resend: "Invia di nuovo",
  },
  reports: {
    title: "Report",
    subtitle: "Visualizza analisi dettagliate su monitoraggio del tempo, progetti e prestazioni del team",
    tabTime: "Report sul tempo",
    tabProjects: "Report sui progetti",
    dateRange: "Intervallo di date",
    exportCsv: "Esporta CSV",
    timeByMember: "Tempo per membro del team",
    timeByMemberDesc: "Ripartizione delle ore registrate da ciascun membro del team",
    selectDateRange: "Seleziona un intervallo di date per visualizzare i report sul tempo",
    colMember: "Membro del team",
    colTotalHours: "Ore totali",
    colBillableHours: "Ore fatturabili",
    colBillablePct: "% fatturabile",
    projectPerformance: "Prestazioni dei progetti",
    projectPerformanceDesc: "Panoramica di tutti i progetti e del loro avanzamento",
    noProjects: "Nessun progetto trovato",
    colProject: "Progetto",
    colHours: "Ore",
    colTasks: "Attività",
    colCompletion: "Completamento",
    colTeamSize: "Dimensione del team",
  },
  workhub: {
    summaryTotal: "Attività totali",
    summaryPastDue: "Scadute",
    summaryAging: "Oltre 30 giorni",
    changeStatus: "Cambia stato dell'attività",
    task: "Attività",
    newStatus: "Nuovo stato",
    blockedDesc: "una dipendenza esterna impedisce l'avanzamento.",
    suspendedDesc: "in pausa per rivalutazione.",
    reason: "Motivo",
    reasonPlaceholder: "Spiega perché questa attività viene messa in pausa...",
    reasonRequired: "È richiesto un motivo per le attività bloccate o sospese.",
    updateStateError: "Impossibile aggiornare lo stato dell'attività",
    unknownError: "Errore sconosciuto",
    submit: "Invia",
    saving: "Salvataggio...",
    cancel: "Annulla",
    saveTimeEntry: "Salva voce di tempo",
    duration: "Durata",
    billableQ: "Fatturabile?",
    billable: "Fatturabile",
    nonBillable: "Non fatturabile",
    notesOptional: "Note (facoltative)",
    notesPlaceholder: "A cosa hai lavorato?",
    saveEntry: "Salva voce",
    saveEntryError: "Impossibile salvare la voce",
    taskActivity: "Attività dell'attività",
    loadingActivity: "Caricamento dell'attività...",
    loadDetailError: "Impossibile caricare i dettagli dell'attività",
    timeEntries: "Voci di tempo ({{count}})",
    noTimeEntries: "Ancora nessuna voce di tempo.",
    invoiced: "Fatturato",
    statusHistory: "Cronologia degli stati ({{count}})",
    noStateChanges: "Nessuna modifica di stato registrata.",
    pmSyncState: "Stato di sincronizzazione PM",
    syncStatus: "Stato:",
    syncUnknown: "sconosciuto",
    lastSyncLabel: "Ultima sincronizzazione: {{time}}",
    retries: "Tentativi: {{count}}",
    errorLabel: "Errore: {{message}}",
    resolved: "Risolto: {{time}}",
    aiBadge: "IA",
    manualBadge: "Manuale",
    running: "in corso",
    title: "Centro di lavoro del consulente",
    subtitle: "Coda di attività unificata con filtro per progetto e monitoraggio del tempo con un clic.",
    allProjects: "Tutti i progetti",
    timerRunning: "Timer in esecuzione",
    stopTimer: "Ferma il timer",
    inactivityTitle: "Il timer è in esecuzione da oltre 8 ore",
    inactivityBody: "È ancora lavoro attivo? Ferma il timer se ti sei allontanato.",
    keepRunning: "Continua",
    loadingQueue: "Caricamento della coda del consulente...",
    loadError: "Impossibile caricare i dati del centro di lavoro.",
    assignedQueue: "Coda di lavoro assegnata",
    noTasks: "Nessuna attività assegnata trovata per questo filtro.",
    compliance: "Conformità",
    activity: "Attività",
    blocked: "Bloccata",
    suspended: "Sospesa",
    stop: "Ferma",
    startTimer: "Avvia il timer",
    activeTask: "Attività attiva",
    billablePreview: "Anteprima fatturabile pronta per la fatturazione",
    complianceFlaggedTasks: "Attività segnalate per conformità: {{count}}",
    loadingPreview: "Caricamento dell'anteprima fatturabile...",
    previewError: "Impossibile caricare l'anteprima fatturabile.",
    previewSummary: "Voci: {{count}} • Totale ore fatturabili: {{hours}}",
    sourceBreakdown: "Ripartizione per origine:",
    manual: "Manuale:",
    ai: "IA:",
    complianceReviewPanel: "Pannello di revisione della conformità",
    loadingFlags: "Caricamento delle segnalazioni di conformità...",
    flagsError: "Impossibile caricare le segnalazioni di conformità.",
    noFlags: "Nessuna segnalazione di conformità aperta.",
  },
  billing: {
    title: "Configurazione della fatturazione",
    subtitle: "Configura le tariffe orarie per il monitoraggio del tempo e la fatturazione. Le tariffe seguono una gerarchia: Utente → Progetto → Predefinita aziendale.",
    fetchError: "Impossibile recuperare le tariffe di fatturazione",
    updateError: "Impossibile aggiornare la tariffa",
    updateSuccess: "Tariffa di fatturazione aggiornata con successo",
    invalidRate: "Inserisci una tariffa valida",
    resolutionTitle: "Ordine di risoluzione delle tariffe:",
    resolutionBody: "Nel calcolo degli importi fatturabili, il sistema controlla le tariffe in questo ordine: 1) tariffa specifica dell'utente, 2) tariffa specifica del progetto, 3) tariffa predefinita aziendale.",
    companyRate: "Tariffa predefinita aziendale",
    companyRateDesc: "Tariffa di riserva utilizzata quando non è impostata alcuna tariffa utente o progetto",
    hourlyRate: "Tariffa oraria (USD)",
    projectRates: "Tariffe specifiche per progetto",
    projectRatesDesc: "Sostituisci la predefinita aziendale per progetti specifici",
    projectIdLabel: "ID progetto: {{id}}",
    userRates: "Tariffe specifiche per utente",
    userRatesDesc: "Sostituisci tutte le altre tariffe per membri del team specifici",
    noTeamMembers: "Nessun membro del team trovato",
    customRate: "Tariffa personalizzata",
    usingDefault: "Usa predefinita",
    defaultPlaceholder: "Predefinita",
  },
  verification: {
    noToken: "Nessun token di verifica fornito. Controlla il link nella tua e-mail.",
    verifiedSuccess: "La tua e-mail è stata verificata con successo!",
    verifyFailed: "Verifica dell'e-mail non riuscita. Il link potrebbe essere scaduto.",
    unexpectedError: "Si è verificato un errore imprevisto. Riprova o contatta l'assistenza.",
    resendFailed: "Invio dell'e-mail di verifica non riuscito. Riprova.",
    networkError: "Errore di rete. Riprova.",
    titleLoading: "Verifica dell'e-mail in corso...",
    titleSuccess: "E-mail verificata!",
    titleError: "Verifica non riuscita",
    descLoading: "Attendi mentre verifichiamo il tuo indirizzo e-mail",
    descSuccess: "Ora puoi accedere al tuo account",
    descError: "Abbiamo riscontrato un problema con la tua verifica",
    signInNow: "Accedi ora",
    resending: "Invio in corso...",
    resendButton: "Invia di nuovo l'e-mail di verifica",
    resentNotice: "E-mail di verifica inviata di nuovo! Controlla la tua casella di posta.",
    backToSignIn: "Torna all'accesso",
    redirecting: "Reindirizzamento all'accesso tra 3 secondi...",
  },
  invitePage: {
    errorNoToken: "Nessun token di invito fornito",
    errorInvalidExpired: "Invito non valido o scaduto",
    errorPasswordMismatch: "Le password non corrispondono",
    errorPasswordTooShort: "La password deve contenere almeno 8 caratteri",
    errorAcceptFailed: "Impossibile accettare l'invito",
    errorUnexpected: "Si è verificato un errore imprevisto",
    invalidTitle: "Invito non valido",
    invalidDescription: "Questo link di invito non è valido o è scaduto.",
    invalidContactPrompt: "Contatta la persona che ti ha invitato per richiedere un nuovo invito.",
    goToSignIn: "Vai all'accesso",
    acceptedTitle: "Benvenuto nel team!",
    acceptedDescription: "Il tuo account è stato creato con successo.",
    acceptedBody: "Ora puoi accedere allo spazio di lavoro wxKanban della tua azienda.",
    signInNow: "Accedi ora",
    joinTitle: "Unisciti a {{companyName}}",
    invitedBy: "Sei stato invitato da {{inviterName}} a unirti come {{role}}",
    emailLabel: "E-mail",
    firstNameLabel: "Nome",
    lastNameLabel: "Cognome",
    passwordLabel: "Password",
    confirmPasswordLabel: "Conferma password",
    creatingAccount: "Creazione dell'account...",
    acceptAndCreate: "Accetta l'invito e crea l'account",
    alreadyHaveAccount: "Hai già un account?",
    signInHere: "Accedi qui",
  },
  inviteAccept: {
    subtitle: "Invito al team",
    validating: "Convalida dell'invito in corso...",
    expiredTitle: "Invito scaduto",
    expiredBody: "Questo link di invito è scaduto. Chiedi al tuo amministratore di inviarlo di nuovo.",
    usedTitle: "Invito già utilizzato",
    invalidTitle: "Invito non valido",
    usedBody: "Questo invito è già stato accettato. Prova ad accedere.",
    invalidBody: "Questo link di invito non è valido o è scaduto.",
    goToLogin: "Vai all'accesso",
    successTitle: "Account creato!",
    successBody: "Il tuo account è stato configurato. Ora puoi accedere a wxKanban.",
    logIn: "Accedi",
    joinCompany: "Unisciti a {{company}}",
    creatingAccountFor: "Creazione dell'account per",
    fullNameLabel: "Nome completo",
    passwordLabel: "Password",
    passwordPlaceholder: "Min. 8 caratteri",
    confirmPasswordLabel: "Conferma password",
    confirmPasswordPlaceholder: "Reinserisci la password",
    timezoneLabel: "Fuso orario",
    timezoneHint: "Rilevato automaticamente dal tuo browser",
    creatingAccount: "Creazione dell'account...",
    createAccount: "Crea account",
    alreadyHaveAccount: "Hai già un account?",
    signIn: "Accedi",
    errorPasswordMismatch: "Le password non corrispondono",
    errorPasswordTooShort: "La password deve contenere almeno 8 caratteri",
    errorCreateAccountFailed: "Impossibile creare l'account",
    errorUnknown: "Errore sconosciuto",
  },
  inviteTeam: {
    title: "Invita membri del team",
    subtitle: "Aggiungi colleghi per collaborare ai tuoi progetti",
    sendInvitations: "Invia inviti",
    sendInvitationsDesc: "Scegli come vuoi invitare i membri del tuo team",
    singleInvite: "Invito singolo",
    bulkUpload: "Caricamento in blocco",
    sendMore: "Invia altri inviti",
    viewTeam: "Visualizza team",
    tipsTitle: "Consigli per invitare i membri del team",
    tipEmails: "Assicurati di usare gli indirizzi e-mail corretti per evitare mancate consegne",
    tipRoles: "Scegli i ruoli appropriati: Amministratore (accesso completo), Manager (può gestire i progetti) o Membro (può visualizzare e completare le attività)",
    tipCsv: "Per i caricamenti in blocco, usa un file CSV con le colonne: email, usertype, firstName, lastName",
    tipExpiry: "Gli inviti scadono dopo 7 giorni se non vengono accettati",
  },
  teamDashboard: {
    title: "La mia dashboard",
    welcomeBack: "Bentornato, {{email}}",
    assignedProjects: "Progetti assegnati",
    openTasks: "Attività aperte",
    hoursThisWeek: "Ore questa settimana",
    noProjectsAssigned: "Nessun progetto ti è ancora stato assegnato.",
    workHubTitle: "Hub di lavoro del consulente",
    workHubDescription: "Apri la tua coda di attività unificata e traccia il tempo direttamente sul lavoro assegnato.",
    openWorkHub: "Apri hub di lavoro",
  },
  distributorSignup: {
    errorPasswordsMismatch: "Le password non corrispondono",
    errorPasswordTooShort: "La password deve contenere almeno 8 caratteri",
    errorAcceptTerms: "Accetta i termini per i distributori",
    errorRegistrationFailed: "Registrazione non riuscita",
    errorUnknown: "Errore sconosciuto",
    successHeading: "Tutto pronto",
    successSignedInPrefix: "Il tuo account distributore gratuito è pronto. Hai effettuato l'accesso come",
    referralCodeLabel: "Il tuo codice di referral",
    copyCodeTitle: "Copia codice",
    referralCodeHelp: "Condividi questo codice con le persone che segnali. Quando lo inseriscono durante la registrazione, il loro account viene accreditato a te.",
    goToDashboard: "Vai alla tua dashboard",
    introBannerBefore: "Diventa distributore wxKanban. Configura il tuo account",
    introBannerFree: "gratuito",
    introBannerAfter: "qui sotto — riceverai un codice di referral da condividere e al termine verrai connesso automaticamente.",
    fieldBusinessName: "Nome dell'azienda *",
    fieldFullName: "Il tuo nome completo *",
    fieldEmail: "Email *",
    fieldPassword: "Password *",
    placeholderPassword: "Almeno 8 caratteri",
    fieldConfirmPassword: "Conferma password *",
    placeholderConfirmPassword: "Reinserisci la password",
    acceptTermsLabel: "Accetto i termini per i distributori. Il mio account è un account gratuito a utente singolo — non ho sottoscritto un piano a pagamento.",
    creatingAccount: "Creazione dell'account…",
    createAccountButton: "Crea account gratuito",
    pageSubtitle: "Registrazione distributore",
  },
  overviewPage: {
    loadFailed: "Impossibile caricare il documento di panoramica ({{status}})",
    unknownError: "Errore sconosciuto",
    dashboard: "Dashboard",
    projectOverview: "Panoramica del progetto",
    refresh: "Aggiorna",
    loadErrorTitle: "Impossibile caricare il documento di panoramica",
  },
  projectWorkspace: {
    // [SCOPE 137 / T019] Change Impact mode (owner-set)
    impactModeTitle: "Modalità d'impatto delle modifiche",
    impactModeHelp: "Come il controllo d'impatto del kit tratta le modifiche dell'IA in questo progetto. Vale per il kit di ogni sviluppatore e prevale su un valore locale.",
    impactModeNotify: "Notifica: dire allo sviluppatore cosa tocca una modifica, senza mai chiedere",
    impactModeBoundaries: "Confini: chiedere solo quando una modifica raggiunge un altro ambito o un test approvato (predefinito)",
    impactModeAlways: "Sempre: chiedere ogni volta che una modifica tocca altro",
    impactModeNotSet: "Non impostato qui. Il kit di ogni sviluppatore usa il proprio valore locale, o Confini.",
    impactModeSetBy: "Impostato da {{who}} il {{when}}",
    impactModeOwnerOnly: "Solo il proprietario del progetto o un amministratore dell'azienda può modificarlo.",
    impactModeDataLoss: "Una possibile perdita di dati viene fermata in ogni modalità.",
    impactModeError: "La modifica non è stata salvata. Riprova.",
    complianceTabSettings: "Impostazioni",
    complianceTabDocuments: "Documenti",
    complianceTabTasks: "Attività",
    complianceTabAudit: "Audit",
    complianceTabReports: "Report",
    learningsGenerateFailed: "Non riuscito ({{status}})",
    learningsPrintHint: "Usa la stampa del browser per salvare come PDF",
    regenerate: "Rigenera",
    learningsIframeTitle: "Apprendimenti del progetto",
    learningsNoneTitle: "Nessun report degli apprendimenti",
    learningsRunPrefix: "Esegui",
    learningsRunSuffix: "dopo aver completato il lavoro per acquisire decisioni, correzioni e controlli di conformità.",
    learningsThenGenerate: "Quindi genera il report qui sotto.",
    generating: "Generazione…",
    generateLearningsReport: "Genera report degli apprendimenti",
    errFailedLoadProject: "Impossibile caricare il progetto",
    confirmArchive: "Archiviare questo progetto? Verrà nascosto dall'elenco dei progetti.",
    confirmDelete: "Eliminare definitivamente \"{{name}}\"? Verranno rimossi il progetto e tutte le sue attività, fasi, specs e documenti. L'operazione non può essere annullata.",
    thisProject: "questo progetto",
    errFailedDeleteProjectStatus: "Impossibile eliminare il progetto ({{status}})",
    errFailedDeleteProject: "Impossibile eliminare il progetto",
    errFailedUpdateProject: "Impossibile aggiornare il progetto",
    errFailedUpdateAuditType: "Impossibile aggiornare il tipo di audit",
    loadingProject: "Caricamento del progetto...",
    projectNotFound: "Progetto non trovato",
    backToProjects: "Torna ai progetti",
    projects: "Progetti",
    clientEngagementTitle: "Accesso tramite incarico attivo con {{name}}",
    client: "Cliente",
    clientProjectBadge: "Progetto cliente — {{name}}",
    editProject: "Modifica progetto",
    auditLabel: "Audit:",
    noAudit: "Nessun audit",
    archiveProject: "Archivia progetto",
    archive: "Archivia",
    deleteProjectTitle: "Elimina definitivamente il progetto",
    delete: "Elimina",
    tabLifecycle: "Ciclo di vita",
    tabDevelopmentPlan: "Piano di sviluppo",
    tabPortfolioAnalyzer: "Analizzatore di portfolio",
    tabCompliance: "Conformità",
    tabLearnings: "Apprendimenti",
    tabKit: "Kit",
    views: "Viste",
    viewMatrix: "Matrice",
    viewStack: "Stack",
    viewTheme: "Tema",
    viewBugReports: "Segnalazioni di bug",
    iframeLifecycle: "Ciclo di vita del progetto",
    iframeDevplan: "Piano di sviluppo",
    phaseNotStarted: "Questa fase non è ancora iniziata.",
    saveFailed: "Salvataggio non riuscito",
    saveErrorIntro: "Si è verificato il seguente errore durante il salvataggio:",
    copyToClipboard: "Copia negli appunti",
    copy: "Copia",
    copyHint: "Fai clic su \"Copia\" per copiare questo messaggio di errore negli appunti e segnalarlo.",
    close: "Chiudi",
    copyAndClose: "Copia e chiudi",
    fieldProjectName: "Nome del progetto",
    fieldDescription: "Descrizione",
    fieldHourlyRate: "Tariffa oraria ($)",
    fieldAllocatedHours: "Ore allocate",
    fieldPmSystems: "Sistemi PM",
    fieldSecurityClassification: "Classificazione di sicurezza",
    fieldAuditType: "Tipo di audit",
    phaseAssignments: "Assegnazioni di fase",
    loadingTeamMembers: "Caricamento dei membri del team...",
    unassigned: "Non assegnato",
    reassignPermissionNote: "Solo ADMIN e TEAM_MANAGER possono riassegnare le fasi.",
    cancel: "Annulla",
    saving: "Salvataggio...",
    saveChanges: "Salva modifiche",
  },
  helpPage: {
    backToHelpCenter: "Torna al centro assistenza",
    examples: "Esempi",
    wasArticleHelpful: "Questo articolo è stato utile?",
    yesItHelped: "Sì, è stato utile",
    noNotHelpful: "No, non è stato utile",
    helpCenter: "Centro assistenza",
    // [SCOPE 138 / T011] "Learn wxKanban with your AI"
    teachTitle: "Impara wxKanban con la tua IA",
    teachHowTo: "Scarica le lezioni, dai start-here.md (o l’intero zip) a qualsiasi chat di IA e di’ «insegnami». Ogni lezione ti guida passo passo in un’attività.",
    teachDownload: "Scarica le lezioni",
    teachVersion: "wxKanban {{version}} · {{count}} lezioni",
    teachError: "Il download non è riuscito. Riprova.",
    subtitle: "Trova risposte, documentazione e guide per wxKanban",
    searchPlaceholder: "Cerca articoli di aiuto, comandi CLI o guide...",
    searchResults: "Risultati della ricerca",
    searching: "Ricerca in corso...",
    noResults: "Nessun risultato trovato per \"{{query}}\"",
    viewsCount: "{{count}} visualizzazioni",
    topicsCount: "{{count}} argomenti",
    moreCount: "+{{count}} altri",
  },
  planHelp: {
    title: "Guida",
    subtitlePrefix: "Cosa fa ogni area di wxKanban e cosa include il tuo piano. Viene mostrato",
    subtitleSuffix: "per impostazione predefinita: scegli un altro piano per confrontare.",
    yourPlan: "il tuo piano",
    included: "Incluso",
    upgradeRequired: "Aggiornamento richiesto",
    loading: "Caricamento del tuo piano…",
  },
  faqPage: {
    title: "Aiuto e FAQ",
    subtitle: "Sfoglia le risposte oppure poni una domanda e la aggiungeremo alle FAQ.",
    panel: {
      heading: "Domande frequenti",
      loading: "Caricamento…",
      empty: "Ancora nessuna FAQ — poni una domanda qui sotto.",
      askPrompt: "Non hai trovato la risposta? Chiedi a noi",
      placeholder: "Scrivi la tua domanda…",
      submit: "Invia domanda",
      sending: "Invio…",
      tooShort: "Inserisci una domanda (almeno 5 caratteri).",
      submitError: "Impossibile inviare la tua domanda.",
      thanks: "Grazie — risponderemo.",
    },
  },
  aboutPage: {
    title: "Informazioni su wxKanban",
    lede: "wxKanban è un modo di sviluppare software con l'IA guidato dalle specifiche: ti insegna a lavorare come i professionisti, portando un'applicazione attraverso l'intero ciclo di vita, da un'idea disordinata a un risultato pulito, testato e rilasciato, su fondamenta che possiedi davvero.",
    whatItIsHeading: "Che cos'è",
    whatItIsPara1: "wxKanban è un ambiente di sviluppo guidato dalle specifiche. Procedi attraverso un ciclo di vita guidato in sei fasi — Progettazione → Implementazione → QA → Test umani → Beta → Rilascio — in cui nulla viene costruito senza una specifica approvata, e ogni modifica è tracciabile fino alla specifica che l'ha autorizzata. Altrettanto importante, mantiene le tue specifiche e la documentazione coerenti man mano che il software evolve: le modifiche future vengono verificate rispetto a ciò che è già scritto, così la tua documentazione si aggiorna con il lavoro invece di diventare obsoleta.",
    whatItIsIntro: "Attorno a quel ciclo di vita, wxKanban ti offre ciò che ti serve per svolgere un lavoro reale in team:",
    teamMgmtLabel: "Gestione del team",
    teamMgmtBody: "— invita persone, imposta i loro ruoli e controlla chi può fare cosa.",
    checkoutLabel: "Check-out e check-in",
    checkoutBody: "— prendi in carico un ambito per lavorarci e riconsegnalo quando hai finito, così il lavoro resta coordinato e nessuno sovrascrive silenziosamente quello di un altro.",
    cockpitLabel: "Il Cockpit",
    cockpitBody: "— un centro di comando in tempo reale per il tuo progetto: a che punto è, cosa viene dopo e ogni comando di wxKanban in un unico posto.",
    helpLabel: "Aiuto attivo e contestuale",
    helpBody: "— indicazioni e risposte proprio dove lavori, sia dentro wxKanban sia dentro VS Code, dove avviene davvero la costruzione.",
    whatItIsClosing: "Questo è il cuore di wxKanban: il ciclo di vita guidato dalle specifiche, con gli strumenti per gestirlo in team.",
    whyHeading: "Perché esiste",
    whyPara1: "L'IA ha cambiato il modo in cui si costruisce il software, ma la parte difficile non è mai stata la sintassi: è stato il ",
    whyEmphasis: "giudizio",
    whyPara2: " per portare un'applicazione fino al rilascio. wxKanban è costruito per offrire agli sviluppatori esperti un modo di portare quel giudizio nell'era dell'IA e per dare ai nuovi arrivati quella disciplina fin dal primo giorno, così che l'IA acceleri il tuo lavoro invece di sfuggirti di mano.",
    groundHeading: "Costruito su un terreno che possiedi",
    groundPara: "I tuoi dati risiedono in un database SQL standard, la tua autenticazione è tua e puoi cambiare fornitore di IA quando vuoi. Nessun blocco proprietario, nessun fornitore che si frappone tra te e il software da cui dipendi.",
    behindHeading: "Chi c'è dietro",
    behindPara1: "wxKanban è realizzato da ",
    behindPara2: ", fondata da Andy “Cowboy” Stapleton, che ha trascorso oltre 40 anni attraversando ogni grande cambiamento di piattaforma, dal mainframe al web fino al cloud, aiutando altri sviluppatori a compiere la stessa traversata.",
    contactHeading: "Mettiti in contatto",
    contactIntro: "Domande o idee? Scrivi a",
    contactOutro: "— rispondiamo davvero.",
    phoneLabel: "Telefono:",
  },
  bioPage: {
    title: "Informazioni sul fondatore",
    photoAlt: "Ritratto del fondatore",
    loading: "Caricamento…",
  },
  privacyPage: {
    title: "Informativa sulla privacy",
    lastUpdated: "Ultimo aggiornamento: {{date}}",
    intro:
      "wxKanban (“noi”, “wxperts”) rispetta la tua privacy. Questa informativa spiega quali dati raccogliamo quando utilizzi l'applicazione wxKanban, perché lo facciamo e quali scelte hai a disposizione. Raccogliamo ciò che ci serve per erogare il servizio, non vendiamo i tuoi dati e puoi chiederci di eliminarli.",
    authoritativeNote:
      "Questa informativa è stata tradotta per comodità. La versione in inglese è quella autorevole.",
    sections: {
      s1heading: "Informazioni che raccogliamo",
      s1item1:
        "Dati dell'account e dell'azienda — nome, indirizzo email, password (memorizzata solo come hash sicuro), nome dell'azienda, ruolo e preferenza di lingua.",
      s1item2:
        "Il lavoro che crei — progetti, attività, specifiche, note, registrazioni delle ore, fatture e contenuti correlati che inserisci nell'app.",
      s1item3:
        "Informazioni di fatturazione — i dettagli di abbonamento e pagamento sono elaborati dal nostro fornitore di pagamenti, Stripe. Non memorizziamo i numeri completi delle carte; conserviamo il tuo piano, lo stato e la cronologia di fatturazione.",
      s1item4:
        "Servizi collegati — se colleghi uno strumento di project management (ad esempio Jira, Monday.com, Asana, Trello), memorizziamo i token di accesso necessari per la sincronizzazione, cifrati a riposo.",
      s1item5:
        "Utilizzo e log — log tecnici di base (come azioni, marche temporali ed errori) utilizzati per gestire, proteggere e migliorare il servizio.",
      s2heading: "Come le utilizziamo",
      s2item1: "Per fornire e gestire l'app — il tuo account, i progetti, il monitoraggio delle ore e la fatturazione.",
      s2item2: "Per elaborare abbonamenti e pagamenti tramite Stripe.",
      s2item3: "Per sincronizzare con gli strumenti di project management che scegli di collegare.",
      s2item4: "Per alimentare le funzionalità assistite dall'IA (vedi sotto).",
      s2item5: "Per proteggere il servizio, prevenire abusi e fornire assistenza.",
      s3heading: "Elaborazione tramite IA",
      s3body:
        "Alcune funzionalità inviano i contenuti che fornisci (ad esempio specifiche o testi correlati) a fornitori di IA di terze parti — come Google Gemini, OpenAI o altri che configuriamo — al solo scopo di generare il risultato richiesto. Non utilizziamo i tuoi contenuti per addestrare i nostri modelli e i fornitori di IA li elaborano secondo i propri termini in qualità di nostri fornitori di servizi.",
      s4heading: "Con chi li condividiamo",
      s4body:
        "Non vendiamo né noleggiamo le tue informazioni. Le condividiamo solo con i fornitori di servizi che ci aiutano a gestire wxKanban — ad esempio l'hosting cloud (Amazon Web Services), l'elaborazione dei pagamenti (Stripe) e i fornitori di IA sopra indicati — e solo affinché possano svolgere tali servizi per noi. Potremmo divulgare informazioni se richiesto dalla legge.",
      s5heading: "Sicurezza",
      s5body:
        "Proteggiamo i tuoi dati con la cifratura in transito e a riposo, password sottoposte ad hashing e controlli di accesso. Nessun sistema è perfettamente sicuro, ma lavoriamo continuamente per salvaguardare le tue informazioni.",
      s6heading: "Per quanto tempo li conserviamo",
      s6body:
        "Conserviamo il tuo account e i contenuti che crei per tutto il tempo in cui l'account è attivo, e per un periodo ragionevole successivo, se necessario per finalità legali, contabili e operative. Puoi chiederci di eliminare i tuoi dati in qualsiasi momento.",
      s7heading: "Le tue scelte e i tuoi diritti",
      s7body:
        "Puoi accedere alle tue informazioni personali, correggerle, esportarle o eliminarle, e revocare il consenso ove applicabile. Se ti trovi in una regione con diritti specifici in materia di protezione dei dati (come l'UE o il Regno Unito ai sensi del GDPR, o le leggi statali statunitensi sulla privacy), tali diritti si applicano a te. Per esercitarli, scrivi a",
      s8heading: "Trasferimenti internazionali",
      s8body:
        "wxKanban è gestito dagli Stati Uniti e le tue informazioni potrebbero essere elaborate lì. Utilizzando il servizio, acconsenti a tale trasferimento, fatte salve le protezioni descritte in questa informativa.",
      s9heading: "Contatti",
      s9body: "Domande sulla privacy? Scrivi a",
    },
  },
  termsPage: {
    title: "Termini di utilizzo",
    lastUpdated: "Ultimo aggiornamento: {{date}}",
    intro:
      "Questi termini disciplinano l'utilizzo dell'applicazione e del sito web wxKanban. Creando un account o utilizzando il servizio, li accetti. Ti invitiamo a leggere anche la nostra",
    authoritativeNote:
      "Questi termini sono stati tradotti per comodità. La versione in inglese è quella autorevole.",
    sections: {
      s1heading: "Il tuo account",
      s1body:
        "Sei responsabile dell'accuratezza dei dati del tuo account e della sicurezza delle tue credenziali. Sei responsabile delle attività che avvengono con il tuo account e dei membri del team che inviti.",
      s2heading: "Uso accettabile",
      s2body:
        "Utilizza wxKanban in modo lecito. Non tentare di interrompere il servizio o di accedervi senza autorizzazione, di utilizzare in modo improprio i dati di altri utenti o di sfruttare la piattaforma per creare o archiviare contenuti illeciti.",
      s3heading: "Abbonamenti e fatturazione",
      s3body:
        "I piani a pagamento vengono fatturati tramite Stripe su base ricorrente fino alla cancellazione. Le tariffe sono addebitate in anticipo per ciascun periodo di fatturazione e non sono rimborsabili, salvo ove richiesto dalla legge. Puoi disdire in qualsiasi momento; il tuo piano resta attivo fino alla fine del periodo in corso. Forniremo un preavviso ragionevole in caso di variazioni di prezzo.",
      s4heading: "I tuoi contenuti e la proprietà",
      s4body:
        "Sei proprietario dei dati e dei contenuti che crei in wxKanban. Ci concedi la licenza limitata necessaria per ospitarli, elaborarli, eseguirne il backup e visualizzarli affinché possiamo fornire il servizio. Puoi esportare o eliminare i tuoi contenuti come descritto nella nostra Informativa sulla privacy.",
      s5heading: "Output generati dall'IA",
      s5body:
        "wxKanban utilizza l'IA per assistere il tuo lavoro. L'output dell'IA può essere imperfetto — sei responsabile della revisione, del collaudo e dell'approvazione di qualsiasi elemento generato prima di farvi affidamento. Non garantiamo che i risultati assistiti dall'IA siano accurati, completi o idonei a uno scopo particolare.",
      s6heading: "La nostra proprietà intellettuale",
      s6body:
        "L'applicazione wxKanban, il suo design e i nomi wxKanban e wxperts appartengono a wxperts o ai suoi licenzianti. Questi termini non ti concedono alcun diritto sul nostro software o marchio oltre all'utilizzo del servizio nei modi previsti.",
      s7heading: "Assenza di garanzie",
      s7body:
        "Il servizio è fornito “così com'è” e “come disponibile”, senza garanzie di alcun tipo. Lavoriamo per mantenere wxKanban affidabile e disponibile, ma non garantiamo un funzionamento ininterrotto o privo di errori.",
      s8heading: "Limitazione di responsabilità",
      s8body:
        "Nella misura massima consentita dalla legge, wxperts non è responsabile per perdite indirette, incidentali o consequenziali, né per la perdita di dati o profitti, derivanti dall'uso — o dall'impossibilità di usare — il servizio.",
      s9heading: "Cessazione",
      s9body:
        "Puoi chiudere il tuo account in qualsiasi momento. Possiamo sospendere o revocare l'accesso in caso di violazione di questi termini o per proteggere il servizio. In caso di cessazione, le disposizioni che per loro natura dovrebbero sopravvivere continueranno ad applicarsi.",
      s10heading: "Legge applicabile",
      s10body:
        "wxperts opera dagli Stati Uniti e questi termini sono disciplinati dalla legge statunitense, senza riguardo ai principi in materia di conflitto di leggi. Se utilizzi il servizio dall'Unione Europea, nulla in questi termini elimina i diritti inderogabili di tutela dei consumatori e di protezione dei dati che ti spettano ai sensi del diritto dell'UE (incluso il GDPR); tali protezioni continuano ad applicarsi a te.",
      s11heading: "Modifiche",
      s11body:
        "Potremmo aggiornare questi termini man mano che il prodotto evolve. La data di “ultimo aggiornamento” riportata sopra indica quando sono stati modificati l'ultima volta; l'uso continuato dopo una modifica ne comporta l'accettazione.",
      s12heading: "Contatti",
      s12body: "Domande su questi termini? Scrivi a",
    },
  },
  adminDashboard: {
    saveFailed: "Salvataggio non riuscito",
    editAuditType: "Modifica tipo di audit",
    complianceFramework: "Framework di conformità",
    none: "— Nessuno —",
    title: "Dashboard amministratore",
    welcomeBack: "Bentornato, {{email}}",
    teamMembers: "Membri del team",
    activeProjects: "Progetti attivi",
    hoursThisWeek: "Ore questa settimana",
    timeBilling: "Tempo e fatturazione",
    timeBillingDesc: "Traccia il tempo, imposta le tariffe di fatturazione e gestisci le ore fatturabili su tutti i progetti.",
    timeBillingPlaceholder: "Le voci di tempo e le tariffe di fatturazione appariranno qui.",
    systemHealth: "Stato del sistema",
    reportsAnalytics: "Report e analisi",
    reportsDesc: "Riepiloghi delle entrate, ore per progetto, redditività e report esportabili.",
    reports: "Report",
    reportsPlaceholder: "I report finanziari appariranno qui una volta registrate le voci di tempo.",
  },
  subscriptionCard: {
    statusActive: "Attivo",
    statusPending: "In sospeso",
    statusSuspended: "Sospeso",
    currentPlan: "Piano attuale",
    teamSeats: "Postazioni del team",
    seatLimitReached: "Limite di postazioni raggiunto — esegui l'upgrade per invitare altri membri.",
    upgradePlan: "Aggiorna piano",
  },
  engagementsWidget: {
    title: "Collaborazioni",
    clientFirms: "{{count}} società di consulenza sui tuoi progetti",
    clientFirmsPlural: "{{count}} società di consulenza sui tuoi progetti",
    clientsServed: "{{count}} cliente che servi",
    clientsServedPlural: "{{count}} clienti che servi",
    viewAll: "Vedi tutto",
    roleHiring: "Assunzione",
    roleServing: "In servizio",
    moreCount: "+ altri {{count}}",
  },
  activityFeed: {
    title: "Feed attività",
    loading: "Caricamento attività recenti...",
    subtitle: "Attività recente del team e dei progetti",
    viewAll: "Vedi tutto",
    empty: "Nessuna attività recente",
    emptyHint: "L'attività apparirà qui man mano che il tuo team lavora",
    byUser: "da {{name}}",
    justNow: "proprio ora",
    minutesAgo: "{{count}} min fa",
    hoursAgo: "{{count}} h fa",
    daysAgo: "{{count}} g fa",
  },
  dailyDigest: {
    title: "Riepilogo giornaliero",
    loadingActivity: "Caricamento dell'attività di oggi...",
    subtitle: "Riepilogo dell'attività del team di oggi",
    configure: "Configura",
    viewAll: "Visualizza tutto",
    noActivity: "Nessuna attività ancora oggi",
    activityWillAppear: "L'attività del team apparirà qui",
    byUser: "di {{name}}",
    moreItems: "+{{count}} altri elementi",
    justNow: "proprio ora",
    minutesAgo: "{{count}} min fa",
    hoursAgo: "{{count}} h fa",
  },
  roleLock: {
    contactAdmin: "Contatta il tuo amministratore per accedere a questa sezione.",
    requiresAdmin: "Richiede un amministratore",
  },
  upgradeLock: {
    requiresPlan: "Richiede il piano {{plan}}",
    upgradePlan: "Aggiorna il piano",
  },
  projectProposals: {
    errorFindDraft: "Impossibile trovare la bozza della panoramica per questa proposta",
    errorOpenEditor: "Impossibile aprire l'editor",
    errorFetch: "Impossibile recuperare le proposte: {{status}}",
    errorLoad: "Impossibile caricare le proposte",
    errorUpdateStatus: "Impossibile aggiornare lo stato",
    rejectionReasonPrompt: "Motivo del rifiuto (obbligatorio):",
    title: "Proposte di progetto",
    addProposal: "Aggiungi proposta",
    loadingProposals: "Caricamento delle proposte...",
    empty: "Ancora nessuna proposta. Aggiungine una per iniziare.",
    status_pending: "In attesa",
    status_good_idea: "Buona idea",
    status_accepted: "Accettata",
    status_rejected: "Rifiutata",
    editTitle: "Apri l'editor della panoramica per questa proposta",
    goodIdea: "Buona idea",
    reject: "Rifiuta",
    accept: "Accetta",
    createProject: "Crea progetto",
    reopen: "Riapri",
    projectCreated: "Progetto creato",
  },
  systemHealth: {
    title: "Stato del sistema",
    checked: "Verificato {{time}}",
    refresh: "Aggiorna",
    runningDiagnostics: "Esecuzione diagnostica…",
    allSystemsOperational: "Tutti i sistemi operativi",
    driftOrMissingDetected: "Rilevata deriva dello schema o tabelle mancanti",
    databaseUnreachable: "Database irraggiungibile",
    database: "Database",
    status: "Stato",
    latency: "Latenza",
    errorLabel: "Errore",
    tableRows: "righe {{table}}",
    server: "Server",
    uptime: "Tempo di attività",
    memory: "Memoria",
    env: "Ambiente",
    diagTime: "Tempo diag",
    pmIntegrationsConnected: "integrazioni PM connesse",
    pmIntegrationConnected: "integrazione PM connessa",
    schemaDriftTables: "Deriva dello schema — {{count}} tabelle",
    schemaDriftTable: "Deriva dello schema — {{count}} tabella",
    missing: "mancante",
    run: "Esegui",
    toFix: "per correggere.",
    missingTables: "Tabelle mancanti ({{count}})",
    toCreateMissingTables: "per creare le tabelle mancanti.",
  },
  projectGrid: {
    opening: "Apertura…",
    lastUpdated: "Ultimo aggiornamento: {{date}}",
    notCreated: "non creato",
    noAudit: "Nessun audit",
    editAuditType: "Modifica tipo di audit",
    outstandingTasks: "Attività in sospeso",
    complete: "Completato",
    inProgress: "In corso",
    taskProgress: "{{total}} attività - {{completed}} completate",
    notStarted: "Non iniziato",
    failedOpenOverview: "Impossibile aprire l'editor della panoramica",
    failedOpenOverviewStatus: "Impossibile aprire l'editor della panoramica ({{status}})",
    titleProjectOverview: "Panoramica del progetto",
    titleProjectLifecycle: "Lifecycle del progetto",
    titleDevelopmentPlan: "Piano di sviluppo",
    titleAuditReport: "Report di audit",
    titleCompoundLearnings: "Apprendimenti cumulati",
    titleProjectScopeAnalysis: "Analisi Scope del progetto",
    titleEntityDiagram: "Diagramma delle entità",
    showDocuments: "Mostra documenti",
    hideDocuments: "Nascondi documenti",
    newProject: "Nuovo",
    allProjects: "Tutti i progetti",
    active: "Attivi",
    branched: "Diramati",
    emptyAdmin: "Nessun progetto attivo per ora. Fai clic su Nuovo per creare il tuo primo progetto.",
    emptyTeam: "Nessun progetto ancora assegnato a te.",
  },
  phaseTabBar: {
    assignedUser: "Utente assegnato",
  },
  designPhase: {
    specifications: "Specifiche",
    clarifiedCount: "{{clarified}} / {{total}} chiarite",
  },
  implementationPhase: {
    tasksHeading: "Attività",
    openCount: "{{n}} aperte",
  },
  regression: {
    loading: "Confronto con le esecuzioni precedenti…",
    failed: "Impossibile creare il confronto",
    title: "Controllo di regressione per {{version}}",
    blocking: "{{count}} bloccanti",
    newlyFailing: "Ha iniziato a fallire",
    newlyFailingHint: "Superato l'ultima volta che è stato eseguito. È questo a bloccare il rilascio.",
    neverPassed: "Mai superato",
    neverPassedHint: "Lavoro in sospeso, non una regressione.",
    stillPassing: "Ancora superato",
    lastPassed: "Ultimo esito positivo in {{version}}",
  },
  testSuite: {
    skipTitle: "Requisito di test",
    skipOn: "I test sono DISATTIVATI per questo progetto.",
    skipOff: "I test sono obbligatori per questo progetto.",
    skipTurnOn: "Salta i test",
    skipReEnable: "Richiedi di nuovo i test",
    skipCancel: "Annulla",
    skipConfirm: "Questo disattiva il gate di rilascio per OGNI ambito di questo progetto. I test obbligatori smetteranno di bloccare e i rilasci risulteranno saltati anziché testati. Viene registrato a tuo nome.",
    skipConfirmYes: "Sì, salta i test",
    skipHint: "Solo amministratori. La modifica viene scritta nel log di audit.",
    skipBannerTitle: "I test sono disattivati per questo progetto",
    skipBannerBody: "I test obbligatori non bloccano i rilasci. I gate riportano «saltato», non «superato».",
    scopeFilter: "Ambito:",
    allScopes: "Tutti gli ambiti ({{count}})",
    loading: "Caricamento della suite di test…",
    loadFailed: "Impossibile caricare la suite di test",
    runFailed: "Impossibile avviare l'esecuzione",
    empty: "Nessun test ancora. Vengono generati durante l'implementazione dell'ambito.",
    cycle: "Versione {{version}}",
    cycleStatus: { open: "In sviluppo", uat: "In UAT", production: "In produzione", superseded: "Sostituita" },
    humanPending: "{{count}} test utente in attesa del rilascio in UAT",
    runGate: "Esegui {{gate}}",
    noItemsForGate: "Nessun test nel gate {{gate}}",
    runRunning: "Esecuzione in corso",
    runStalled: "Esecuzione bloccata — nessuna risposta dall'esecutore",
    runTotals: "{{passed}} superati · {{failed}} falliti · {{total}} totali",
    required: "Obbligatorio",
    origin: { ai: "IA", user: "Tester" },
    inherit: "Eredita", override: "Sovrascrivi",
    forceInherited: "Eredita l'obbligatorietà dell'ambito",
    forceOverridden: "Sovrascrive l'obbligatorietà dell'ambito",
    forceScopeTitle: "Test obbligatorio",
    forceScopeLabel: "Richiedi l'approvazione di tutti i {{count}} test di questo ambito",
    forceScopeHint: "Se attivo, il prodotto non può avanzare verso la produzione senza l'approvazione di questi test.",
    filter: { all: "Tutti", outstanding: "In sospeso", forced: "Obbligatori", failing: "Falliti" },
  },
  userTest: {
    loading: "Caricamento dei tuoi test…",
    loadFailed: "Impossibile caricare i tuoi test",
    signoffFailed: "Impossibile registrare",
    dismissFailed: "Impossibile annullare la richiesta di ripetizione",
    notReleasedTitle: "Non ancora pronto per te",
    notReleasedBody: "{{count}} test in attesa. Vengono rilasciati quando i test automatici di questa versione passano.",
    summary: "{{required}} obbligatori · {{optional}} facoltativi · versione {{version}}",
    required: "Obbligatorio", optional: "Facoltativo",
    testerAuthored: "Aggiunto da un tester",
    addTest: "Aggiungi un test",
    preconditions: "Prima di iniziare", steps: "Passaggi", postconditions: "Verifica dopo",
    action: "Fai questo", expected: "Dovresti vedere",
    pass: "Superato", fail: "Fallito",
    notesPlaceholder: "Cosa è successo? (obbligatorio se fallito)",
    failNeedsReason: "Indica cosa è successo davvero — diventerà la segnalazione di errore.",
    failHint: "Un fallimento crea una segnalazione con questi passaggi allegati.",
    bugFiled: "Segnalazione creata con i tuoi passaggi e quanto hai osservato.",
    viewBug: "Apri la segnalazione",
    retestTitle: "Ripeti questo test",
    retestImpact: "Il codice coperto da questo test è cambiato: {{path}} (a {{hops}} passo/i).",
    retestBroad: "È arrivata una modifica molto estesa, quindi tutti i test utente vengono ricontrollati.",
    retestUndetermined: "Non è stato possibile stabilire se la modifica riguardi questo test, perciò te lo chiediamo.",
    dismissRetest: "Non interessato",
    dismissExplain: "Se la modifica non può influire su ciò che hai verificato, spiega perché. La tua approvazione precedente resta valida.",
    dismissPlaceholder: "Perché questa modifica non riguarda questo test",
    confirmDismiss: "Annulla ripetizione",
  },
  addTest: {
    title: "Aggiungi un test da ciò che hai trovato",
    intro: "Hai trovato qualcosa che la specifica non aveva previsto. Annotalo perché venga verificato a ogni rilascio.",
    whatHappened: "Cosa dovrebbe essere vero",
    titlePlaceholder: "es. Cambiare fase con un timer attivo mantiene corretta la durata",
    setup: "Preparazione (una per riga)",
    setupPlaceholder: "es. Un progetto con un timer in esecuzione",
    steps: "Passaggi",
    actionPlaceholder: "Fai questo…",
    expectedPlaceholder: "Dovresti vedere… (un valore, un conteggio, un messaggio)",
    addStep: "Aggiungi un passaggio",
    after: "Verifica dopo (una per riga)",
    afterPlaceholder: "es. Il totale delle ore registrate non cambia",
    vagueWarning: "Il passaggio o i passaggi {{steps}} dicono in sostanza \"funziona\". Indica cosa si deve vedere: un valore, un conteggio o un messaggio.",
    needTitle: "Dai un titolo al test",
    needStep: "Aggiungi almeno un passaggio",
    needExpected: "Ogni passaggio richiede un risultato atteso",
    failed: "Impossibile salvare il test",
    cancel: "Annulla", save: "Salva test",
  },
  qaPhase: {
    testTasks: "Attività di test",
    passCount: "{{n}} superato",
    failCount: "{{n}} fallito",
  },
  humanTestingPhase: {
    feedbackItems: "Elementi di feedback",
    issuesRaisedByTesters: "Problemi segnalati dai tester umani",
    openCount: "{{count}} aperto/i",
  },
  betaPhase: {
    approvalTitle: "Fase Beta — Approvazione dell'amministratore richiesta",
    approvalBody: "La creazione di nuove attività durante la fase Beta richiede l'approvazione esplicita dell'amministratore. Questa operazione verrà registrata nel registro di audit.",
    approveAndCreate: "Approva e crea",
    feedbackTitle: "Feedback Beta",
    feedbackHint: "Le modifiche richiedono l'approvazione dell'amministratore",
    openCount: "{{count}} aperto/i",
    creatingApprovedTask: "Creazione dell'attività approvata...",
  },
  releasePhase: {
    statusComplete: "Completato",
    statusInProgress: "In corso",
    statusReopened: "Riaperto",
    statusPending: "In attesa",
    manualDeployment: "Distribuzione manuale",
    ciCdPipeline: "Pipeline CI/CD",
    stagedRollout: "Rilascio graduale",
    hotfix: "Correzione rapida",
    releaseRecord: "Scheda di rilascio",
    priorPhasesIncomplete: "Fasi precedenti incomplete",
    versionNumber: "Numero di versione",
    versionPlaceholder: "es. v1.0.0",
    releaseDate: "Data di rilascio",
    releaseMethod: "Metodo di rilascio",
    releaseNotes: "Note di rilascio",
    notesPlaceholder: "Riassumi cosa è stato rilasciato, le modifiche principali, i problemi noti...",
    saved: "Salvato",
    saveNotes: "Salva note",
    fullLifecycleSummary: "Riepilogo completo del ciclo di vita",
    columnPhase: "Fase",
    columnStatus: "Stato",
    columnEntered: "Iniziata",
    columnCompleted: "Completata",
    projectLabel: "Progetto",
  },
  phaseCompletionBar: {
    advanceFailed: "Impossibile avanzare la fase",
    reopenFailed: "Impossibile riaprire la fase",
    checkingCriteria: "Verifica dei criteri di completamento...",
    phaseComplete: "Fase completata",
    phaseReopened: "Fase riaperta",
    readyToAdvance: "Pronto ad avanzare",
    criteriaMet: "{{pass}} di {{total}} criteri soddisfatti",
    collapse: "Comprimi",
    expand: "Espandi",
    advancing: "Avanzamento...",
    markAsReleased: "Segna come rilasciato",
    advancePhase: "Avanza fase",
    reopenedForRevisions: "Questa fase è stata riaperta per revisioni.",
    cancelReopen: "Annulla riapertura",
    reopenPhase: "Riapri fase",
    reopenReasonPlaceholder: "Motivo della riapertura di questa fase...",
    reopening: "Riapertura...",
    confirmReopen: "Conferma riapertura",
  },
  phaseTaskGroup: {
    unassigned: "Non assegnato",
    unnamedScope: "Scope senza nome",
    noTasksInScope: "Nessuna attività in questo scope.",
    noTasksInPhase: "Nessuna attività in questa fase.",
    unscopedTasks: "Attività senza scope",
    failedToLoad: "Impossibile caricare le attività",
    requestFailed: "Richiesta non riuscita: {{status}}",
  },
  taskList: {
    statusOpen: "Aperto",
    statusInProgress: "In corso",
    statusCompleted: "Completato",
    statusDismissed: "Scartato",
    resultNone: "—",
    resultPass: "Superato",
    resultFail: "Fallito",
    resultBlocked: "Bloccato",
    priorityLow: "Bassa",
    priorityMed: "Med.",
    priorityHigh: "Alta",
    priorityCrit: "Crit.",
    taskNamePlaceholder: "Nome dell'attività...",
    descriptionPlaceholder: "Descrizione (facoltativa)...",
    addTaskButton: "Aggiungi attività",
    addTask: "Aggiungi attività",
    createError: "Impossibile creare l'attività",
    noTasks: "Nessuna attività ancora",
    colTask: "Attività",
    colResult: "Risultato",
    colRaisedBy: "Segnalato da",
    colPriority: "Priorità",
    colStatus: "Stato",
    countOpen: "{{n}} aperte",
    countClosed: "{{n}} chiuse",
    countTotal: "{{n}} in totale",
  },
  phase: {
    design: "Progettazione",
    implementation: "Implementazione",
    qa: "QA",
    humanTesting: "Test umani",
    beta: "Beta",
    release: "Rilascio",
  },
  projectPanel: {
    saveFailed: "Salvataggio non riuscito",
    failedToLoad: "Caricamento non riuscito",
    loadingTasks: "Caricamento attività…",
    noTasksForScope: "Nessuna attività per questo scope.",
    filterAll: "tutte",
    filterOpen: "aperte",
    filterClosed: "chiuse",
    noFilterTasks: "Nessuna attività {{filter}}.",
    openTasks: "Attività aperte",
    noOpenTasks: "Nessuna attività aperta.",
    statTotal: "Totale",
    statOpen: "Aperto",
    statClosed: "Chiuso",
    statInProgress: "In corso",
    statComplete: "Completato",
    statActive: "Attivo",
    statResolved: "Risolto",
    statScopesPassing: "Scope superati",
    statQaTasksDone: "Attività QA fatte",
    statNoTasks: "Nessuna attività",
    documents: "Documenti",
    completeCheck: "✓ completato",
    tasksCount: "Attività ({{done}}/{{total}})",
    specOpenClosed: "Spec {{num}}: Aperte {{open}} · Chiuse {{closed}}",
    // [SCOPE 137 / T014] Change Impact agreement log (read-only)
    changeImpactTitle: "Registro Change Impact",
    changeImpactCount: "Registro Change Impact ({{count}})",
    changeImpactLoading: "Caricamento…",
    changeImpactEmpty: "Nessun accordo o attraversamento registrato per questo scope.",
    changeImpactError: "Impossibile caricare il registro Change Impact.",
    changeImpactKindAck: "Riconosciuto",
    changeImpactKindApproval: "Approvato",
    changeImpactKindDeclined: "Rifiutato",
    changeImpactKindCrossing: "Attraversato senza supervisione",
    changeImpactAffected: "{{count}} interessati",
    changeImpactQueued: "inviato dopo un periodo offline",
    changeImpactReadOnly: "Sola lettura. Questi record non possono essere modificati.",
    reopen: "Riapri",
    reactivate: "Riattiva",
    markClarified: "Segna chiarito",
    defer: "Rinvia",
    needsClarification: "Richiede chiarimenti",
    deleteScope: "Elimina",
    deleteScopeConfirm: "Eliminare definitivamente lo scope {{num}} \"{{title}}\" e le sue {{tasks}} attività e {{docs}} documento/i? Questa azione non può essere annullata.",
    deleteScopeError: "Impossibile eliminare lo scope. Riprova.",
    overviewDoc: "Doc panoramica",
    scopeCountOne: "{{n}} scope",
    scopeCountOther: "{{n}} scope",
    fullyGenerated: "{{n}} completamente generati",
    noScopesCreatedYet: "Nessuno scope creato finora.",
    scopeDocuments: "Documenti di scope",
    noScopes: "Nessuno scope.",
    progressByScope: "Avanzamento per scope",
    qaStatusByScope: "Stato QA per scope",
    allTestsPassed: "Tutti i {{n}} test superati",
    inProgressDone: "{{n}} in corso · {{done}}/{{total}} fatte",
    completeCount: "{{done}}/{{total}} completate",
    noQaTasks: "Nessuna attività QA",
    betaApprovalNotice: "Le nuove attività in Beta richiedono l'approvazione di un ADMIN prima della creazione.",
    feedbackByScope: "Feedback per scope",
    allScopesClear: "Tutti gli scope liberi — pronto per il rilascio.",
    openItemsRemain: "Restano {{n}} elementi aperti negli scope.",
    phaseChecklist: "Lista di controllo delle fasi",
    openItemsByScope: "Elementi aperti per scope",
    openResolved: "{{open}} aperti · {{done}}/{{total}} risolti",
    phaseView: "Vista {{phase}}",
    allProjects: "Tutti i progetti",
    open: "Apri",
    status: {
      draft: "Bozza",
      inReview: "In revisione",
      pending: "In sospeso",
      clarified: "Chiarito",
      planned: "Pianificato",
      tasksGenerated: "Attività generate",
      implemented: "Implementato",
      qaPassed: "QA superata",
      humanTested: "Testato manualmente",
      betaCleared: "Beta approvata",
      released: "Rilasciato",
      deferred: "Rinviato",
    },
    doctype: {
      tasks: "Attività",
      datamodel: "Modello dati",
      quickstart: "Avvio rapido",
      checklist: "Lista di controllo",
      contracts: "Contratti",
      research: "Ricerca",
      overview: "Panoramica",
      stackStyle: "Stack e stile",
    },
    issuetype: {
      bug: "Bug",
      feature: "Funzionalità",
      enhancement: "Miglioramento",
      task: "Attività",
    },
  },
  helpCatalog: {
    ui: {
      included: "Incluso",
      upgrade: "Aggiorna",
      admin: "Admin",
      adminOnly: "Solo amministratore",
      howToLabel: "Come fare:",
      howToUseIt: "Come usarlo",
      availableFromPrefix: "Disponibile dal piano ",
      availableFromSuffix: ".",
      yourPlan: "Il tuo piano",
      subscriptionPlan: "Piano di abbonamento",
    },
    category: {
      coreWorkspace: "Area di lavoro principale",
      developmentLifecycle: "Ciclo di vita dello sviluppo",
      integrations: "Integrazioni",
      teamCollaboration: "Team e collaborazione",
      timeBillingConsulting: "Tempo, fatturazione e consulenza",
      adminConfiguration: "Amministrazione e configurazione",
      securityCompliance: "Sicurezza e conformità",
      enterpriseSupport: "Enterprise e supporto",
    },
    plan: {
      SINGLE_USER: {
        label: "Utente singolo",
        userLimit: "1 utente",
      },
      SMALL_BUSINESS: {
        label: "Piccola/Media impresa",
        userLimit: "Fino a 5 utenti",
      },
      CONSULTANT: {
        label: "Consulente",
        userLimit: "Team illimitato",
      },
      ENTERPRISE: {
        label: "Enterprise",
        userLimit: "25+ utenti",
      },
    },
    feature: {
      dashboard: {
        title: "Dashboard",
        description: "La panoramica dei tuoi progetti — l'avanzamento delle fasi del Lifecycle per ogni progetto, oltre a collegamenti rapidi ai documenti generati di ciascun progetto.",
        howTo: "Apri il Dashboard dalla barra laterale. Ogni scheda di progetto mostra l'avanzamento per fase e i collegamenti ai documenti; gli ADMIN hanno un pulsante Nuovo per creare un progetto direttamente.",
      },
      projects: {
        title: "Progetti",
        description: "Crea e gestisci progetti. Progetti illimitati in tutti i piani a pagamento.",
        howTo: "Crea un progetto con il pulsante Nuovo della dashboard o dal menu Progetti, poi aprilo per vederne le schede (panoramica, lifecycle, attività).",
      },
      tasks: {
        title: "Le mie attività (Kanban)",
        description: "Il lavoro a te assegnato su una bacheca Kanban che copre tutti i progetti.",
        howTo: "Apri Le mie attività; trascina le schede tra le colonne per cambiarne lo stato, oppure apri una scheda per i dettagli.",
      },
      settings: {
        title: "Impostazioni",
        description: "Impostazioni di account, azienda e preferenze, inclusa la lingua.",
        howTo: "Apri Impostazioni dal menu; qui si trovano il selettore della lingua e il profilo.",
      },
      faq: {
        title: "FAQ",
        description: "Risposte alle domande comuni sull'uso di wxKanban.",
        howTo: "Apri le FAQ dal menu.",
      },
      lifecycle: {
        title: "Lifecycle a 6 fasi e orchestratore",
        description: "La metodologia Design → Implementation → QA → HumanTesting → Beta → Release che regola ogni funzionalità e mantiene un'unica fonte di verità sullo stato di un progetto.",
        howTo: "Guidalo da Claude Code + il kit wxKanban in VS Code (buildscope → createSpecs → implement). Il comando giusto è disponibile solo nella fase giusta.",
      },
      specs: {
        title: "Spec e Scope",
        description: "I documenti Scope-of-project e le Spec, i piani e le attività generati per ogni funzionalità.",
        howTo: "Redigi uno Scope con buildscope; genera Spec, piano e attività con createSpecs; monitorali nel Cockpit.",
      },
      audit: {
        title: "Rapporti di audit",
        description: "Una traccia di audit per ogni release di ciò che è stato costruito, testato e delimitato con i fence di codice.",
        howTo: "Apri il collegamento Rapporto di audit su una scheda di progetto, oppure rigeneralo dalla dashboard Admin.",
      },
      cockpit: {
        title: "Dev Cockpit",
        description: "Una barra laterale di VS Code che mostra il lavoro rimanente per Scope, in tempo reale man mano che il lavoro procede.",
        howTo: "Installa l'estensione Dev Cockpit; fissa il tuo Scope attivo ed elenca le attività non completate.",
      },
      pmIntegrations: {
        title: "Integrazioni PM",
        description: "Collega Jira, Monday.com, Asana e Trello e sincronizza le attività in entrambe le direzioni.",
        howTo: "Apri Integrazioni PM e connettiti tramite OAuth; la sincronizzazione viene eseguita secondo una pianificazione in background.",
      },
      team: {
        title: "Gestione del team",
        description: "Invita i colleghi, assegna i ruoli e gestisci gli accessi con controlli basati sui ruoli.",
        howTo: "Apri Team; invita via e-mail e imposta il ruolo di ciascun membro.",
      },
      timeTracking: {
        title: "Monitoraggio del tempo e fatturazione",
        description: "Timer integrato con monitoraggio delle ore fatturabili e voci di tempo.",
        howTo: "Avvia il timer su un'attività; contrassegna le voci come fatturabili per la fatturazione.",
      },
      invoices: {
        title: "Fatture",
        description: "Genera fatture PDF dalle voci di tempo fatturabili.",
        howTo: "Apri Fatture, scegli un progetto e un intervallo di date e genera; le voci fatturate vengono contrassegnate per evitare duplicati.",
      },
      reports: {
        title: "Report",
        description: "Reportistica avanzata su tempo, progetti e risultati.",
        howTo: "Apri Report dal menu.",
      },
      workHub: {
        title: "Work Hub",
        description: "Un hub degli incarichi per gestire il lavoro svolto per i clienti.",
        howTo: "Apri il Work Hub per visualizzare e gestire i tuoi incarichi.",
      },
      clientPortal: {
        title: "Portale clienti",
        description: "Offri ai clienti una vista limitata e in sola lettura del loro progetto.",
        howTo: "Abilita il portale per un progetto e condividi il link del cliente.",
      },
      customWorkflows: {
        title: "Flussi di lavoro personalizzati",
        description: "Adatta i flussi di lavoro del Lifecycle al processo del tuo team.",
        howTo: "Configura flussi di lavoro personalizzati nelle impostazioni del progetto.",
      },
      apiAccess: {
        title: "Accesso API",
        description: "Accesso programmatico ai tuoi dati wxKanban tramite API.",
        howTo: "Crea un token API nelle Impostazioni e richiama gli endpoint documentati.",
      },
      stackProfiles: {
        title: "Profili di Stack",
        description: "Definizioni riutilizzabili di Stack di destinazione e di stile applicate alla creazione di un progetto.",
        howTo: "Apri i Profili di Stack (Admin) per definire o modificare gli stack della tua azienda.",
      },
      themeProfiles: {
        title: "Profili di tema",
        description: "Temi grafici riutilizzabili per gli output generati.",
        howTo: "Apri i Profili di tema (Admin) per definire o modificare i temi.",
      },
      encryption: {
        title: "Crittografia a riposo",
        description: "I segreti sono crittografati a riposo con AES-256-GCM.",
        howTo: "Sempre attivo — nessuna configurazione richiesta.",
      },
      soc2: {
        title: "SOC 2 e HIPAA",
        description: "Controlli e strumenti di conformità allineati a SOC 2 e HIPAA.",
        howTo: "Aggiungi i documenti SDLC della tua azienda per l'analisi di conformità; assegna i controlli ai membri del team.",
      },
      sso: {
        title: "Single Sign-On (SSO)",
        description: "SSO aziendale per la tua organizzazione.",
        howTo: "Contatta il tuo referente wxKanban per configurare il tuo provider di identità.",
      },
      advancedSecurity: {
        title: "Sicurezza avanzata",
        description: "Controlli di sicurezza aggiuntivi di livello enterprise.",
        howTo: "Configurato come parte dell'onboarding Enterprise.",
      },
      emailSupport: {
        title: "Assistenza via e-mail",
        description: "Assistenza sul prodotto via e-mail.",
        howTo: "Invia un'e-mail a admin@wxperts.com.",
      },
      dedicatedSupport: {
        title: "Assistenza dedicata",
        description: "Un contatto di assistenza dedicato per il tuo account.",
        howTo: "Assegnato durante l'onboarding Enterprise.",
      },
      customIntegrations: {
        title: "Integrazioni personalizzate",
        description: "Integrazioni su misura realizzate per la tua organizzazione.",
        howTo: "Definisci l'ambito delle integrazioni personalizzate con il tuo referente wxKanban.",
      },
      sla: {
        title: "Garanzia SLA",
        description: "Un SLA contrattuale su disponibilità e tempi di risposta.",
        howTo: "Incluso nel tuo contratto Enterprise.",
      },
    },
  },
  // [SCOPE 052] Pagine pubbliche Aiuto/Tutorial (/docs, /docs/:slug)
  docs: {
    title: "Aiuto/Tutorial",
    backHome: "← Torna alla home",
    sectionsLabel: "Sezioni di aiuto",
    navFaq: "FAQ",
    navVideos: "Video",
    navSessions: "Sessioni di formazione",
    navGuides: "Guide",
    guidesHeading: "Guide",
    guidesSubtitle: "Guide pratiche scaricabili da tenere insieme al tuo progetto.",
    guidesConversionTitle: "Guida alla conversione WINDEV / WEBDEV",
    guidesConversionSummary: "Come portare un'applicazione WINDEV o WEBDEV esistente su uno stack moderno React e PostgreSQL, passo dopo passo.",
    guidesDraftBadge: "Bozza",
    guidesDraftNote: "Questa guida è ancora una bozza. I passaggi sono utilizzabili, ma testo e dettagli potrebbero cambiare.",
    guidesLoading: "Caricamento delle guide…",
    guidesEmpty: "Non è ancora disponibile nessuna guida.",
    guidesLanguageLabel: "Lingua",
    guidesLanguageAll: "Tutte le lingue",
    faqHeading: "Domande frequenti",
    faqSubtitle: "Risposte alle domande più comuni su wxKanban.",
    faqEmpty: "Nessuna FAQ è ancora disponibile.",
    sessionsHeading: "Prossime sessioni di formazione online",
    sessionsSubtitle: "Partecipa a una sessione dal vivo con un istruttore per iniziare rapidamente.",
    sessionsEmpty: "Al momento non è programmata alcuna sessione.",
    sessionsRegister: "Manifesta il tuo interesse e ti avviseremo all'apertura della prossima sessione.",
    sessionsCta: "Contattaci per registrarti",
    sessionsJoin: "Iscriviti",
    videosHeading: "Video di formazione",
    videosSubtitle: "Guarda i tutorial passo passo di wxKanban.",
    videosEmpty: "Non è ancora disponibile alcun video di formazione.",
    loading: "Caricamento…",
    loadError: "Impossibile caricare la documentazione. Riprova più tardi.",
    backAll: "← Tutta la documentazione",
    detailError: "Impossibile caricare questo documento. Riprova più tardi.",
    notFoundTitle: "Non trovato",
    notFoundBody: "Questo documento non esiste o non è ancora pubblicato.",
  },
  // [SCOPE 131 / T013] Suspend & cancel copy (FR-011, FR-009).
  subscriptionLifecycle: {
    sectionTitle: "Sospendi o disdici",
    sectionDesc: "Metti in pausa la fatturazione per un periodo, oppure disdici l'abbonamento alla fine del periodo che hai già pagato.",
    stateActive: "Il tuo abbonamento è attivo.",
    statePaused: "La fatturazione è sospesa.",
    statePausedDetail: "Non viene emessa alcuna fattura e nessun dato è stato eliminato. Riprendi quando vuoi: i tuoi progetti e la tua cronologia ti aspettano.",
    stateCancelPending: "Disdetta programmata.",
    stateCancelPendingDetail: "Mantieni l'accesso completo fino al {{date}}. Puoi annullare la disdetta in qualsiasi momento prima di quella data.",
    effectiveDateUnknown: "la fine del periodo di fatturazione in corso",
    pauseTitle: "Sospendi la fatturazione",
    pauseDesc: "Stripe smette di fatturarti e il tuo team perde l'accesso fino alla ripresa. Nulla viene eliminato e nulla è programmato per l'eliminazione.",
    pauseAction: "Sospendi la fatturazione",
    unpauseAction: "Riprendi la fatturazione",
    cancelTitle: "Disdici l'abbonamento",
    cancelDesc: "Il tuo abbonamento termina allo scadere del periodo che hai già pagato. Fino ad allora mantieni l'accesso completo.",
    cancelAction: "Disdici l'abbonamento",
    undoCancelAction: "Mantieni il mio abbonamento",
    pauseSuccess: "Fatturazione sospesa. Riprendi quando vuoi.",
    unpauseSuccess: "Fatturazione ripresa: bentornato.",
    cancelSuccess: "Il tuo abbonamento terminerà il {{date}}. Puoi annullare la disdetta fino ad allora.",
    undoCancelSuccess: "Disdetta annullata. Il tuo abbonamento prosegue normalmente.",
    actionFailed: "L'operazione non è andata a buon fine e nulla è stato modificato. Riprova.",
    dialogTitle: "Disdire l'abbonamento?",
    dialogBody: "Il tuo abbonamento resterà attivo fino al {{date}}. Dopo, il tuo team perderà l'accesso e 30 giorni più tardi i dati della tua azienda verranno archiviati.",
    dialogUndoNote: "Puoi annullare la disdetta in qualsiasi momento prima del {{date}}.",
    dialogTypeLabel: "Digita {{name}} per confermare",
    dialogPlaceholder: "Nome dell'azienda",
    dialogConfirm: "Disdici l'abbonamento",
    dialogKeep: "Mantieni il mio abbonamento",
    dialogMismatch: "Non corrisponde al nome della tua azienda.",
    wallPausedTitle: "La fatturazione è sospesa",
    wallPausedBody: "La tua azienda ha sospeso la propria fatturazione, quindi il prodotto è in pausa. Nulla è stato eliminato. Un amministratore può riprenderla da Impostazioni → Fatturazione e abbonamento.",
    wallPausedCta: "Vai alle impostazioni di fatturazione",
    wallPausedNonAdmin: "La tua azienda ha sospeso la fatturazione. Nulla è stato eliminato: chiedi al tuo amministratore di riprenderla.",
    emailSubject: "Il tuo abbonamento wxKanban terminerà il {{date}}",
    emailHeading: "La tua disdetta è confermata",
    emailBody: "Abbiamo programmato la fine del tuo abbonamento wxKanban per il {{date}}. Mantieni l'accesso completo a {{company}} fino a quella data e non ti verrà addebitato altro.",
    emailUndoHeading: "Hai cambiato idea?",
    emailUndoBody: "Puoi annullare la disdetta in qualsiasi momento prima del {{date}} da Impostazioni → Fatturazione e abbonamento. Nulla viene eliminato finché la disdetta è in attesa.",
    emailFooter: "Se non hai richiesto tu questa operazione, contatta subito l'amministratore della tua azienda.",
  },
  // [SCOPE 005 / T019] Time & Billing (SPEC-005). "Resource", never "AI" (constraint 5).
  timeBilling: {
    common: {
      cancel: "Annulla",
      close: "Chiudi",
      delete: "Elimina",
      edit: "Modifica",
      loading: "Caricamento…",
      save: "Salva",
      saving: "Salvataggio…",
    },
    page: {
      title: "Tempo e fatturazione",
      open: "Apri Tempo e fatturazione",
      manualOnly: "Il tempo delle Risorse proveniente dalle esecuzioni degli agenti non è ancora disponibile. Inserisci il tempo delle Risorse manualmente.",
      engagedManual: "Nei progetti cliente in cui sei coinvolto, il tempo delle Risorse si registra solo con inserimento manuale.",
      generateStatement: "Genera estratto conto",
      billedTotal: "Progetti fatturati in questo intervallo: {{hours}} h",
      actionFailed: "Operazione non riuscita. Riprova.",
      loadFailed: "Impossibile caricare i tuoi progetti.",
    },
    range: {
      from: "Dal",
      to: "Al",
      thisWeek: "Questa settimana",
      includeInvoiced: "Includi il tempo già fatturato",
      invalid: "La data di inizio deve essere uguale o precedente alla data di fine.",
    },
    projects: {
      none: "Nessun progetto per ora.",
      clientProject: "Cliente: {{client}}",
      hours: "{{hours}} h",
      billThis: "Fatturabile",
      entries: "Voci",
      rates: "Tariffe",
      client: "Cliente",
      noClient: "— Nessun cliente —",
      billedTo: "Fatturato a {{client}}",
      assignClient: "Assegna un cliente per fatturare questo progetto",
    },
    lines: {
      line: "Riga",
      scope: "Scope",
      developer: "Sviluppatore",
      resource: "Risorsa",
      developerHours: "h Sviluppatore",
      resourceHours: "h Risorsa",
      actions: "Azioni",
      addDeveloper: "Aggiungi tempo Sviluppatore",
      addResource: "Aggiungi tempo Risorsa",
      noScopes: "Questo progetto non ha ancora scope.",
      deletedScope: "Scope eliminato",
      archived: "archiviato",
      brainstorming: "Brainstorming",
      processes: "Processi",
      dba: "DBA",
      management: "Gestione",
      meetings: "Riunioni",
      projectTotal: "Totale progetto",
    },
    entry: {
      addDeveloper: "Aggiungi tempo Sviluppatore",
      addResource: "Aggiungi tempo Risorsa",
      editTitle: "Modifica voce di tempo",
      person: "Chi ha svolto il lavoro",
      model: "Modello",
      date: "Data",
      time: "Tempo",
      timeHelp: "Inserisci 1:38, 1.5 o 45m. Un segno meno iniziale indica una correzione.",
      savedAs: "Salvato come {{hours}} h (arrotondato ai 15 minuti successivi).",
      notes: "Note",
      correctionNeedsNote: "Una correzione (tempo negativo) richiede una nota.",
      saveFailed: "Impossibile salvare la voce.",
    },
    entries: {
      title: "Voci di tempo — {{project}}",
      none: "Nessuna voce in questo intervallo.",
      who: "Chi / modello",
      hours: "h",
      invoiced: "Fatturato",
      invoicedHelp: "Questa voce è inclusa in un estratto conto e non può più essere modificata. Aggiungi una correzione in un periodo successivo.",
      confirmDelete: "Eliminare questa voce?",
      deleteFailed: "Impossibile eliminare la voce.",
      loadFailed: "Impossibile caricare le voci.",
    },
    timer: {
      title: "Timer",
      idle: "Nessun timer in corso. Premi l'orologio di un progetto, o ▶ su una riga, per avviarne uno.",
      startTitle: "Avvia timer",
      chooseLine: "Riga",
      startHelp: "Ogni avvio e arresto salva una voce a sé, così puoi registrare più intervalli nello stesso giorno.",
      runningHere: "Il timer è in corso su questo progetto.",
      start: "Avvia timer",
      stop: "Ferma e salva",
      alreadyRunning: "Un timer è già in corso.",
      willSave: "Verrà salvato come {{hours}} h.",
      autoNotes: "Esecuzioni degli agenti durante questo timer",
      autoUnavailable: "Le esecuzioni degli agenti non vengono ancora registrate.",
      noRuns: "Nessuna esecuzione di agenti per ora.",
      refresh: "Aggiorna le esecuzioni degli agenti",
      manualNotes: "Le tue note",
      manualPlaceholder: "es. chiamata di 15 minuti con il cliente sullo scope dell’audit",
      notesFailed: "Impossibile caricare le esecuzioni degli agenti.",
      stopFailed: "Impossibile fermare il timer.",
      pause: "Pausa",
      resume: "Riprendi",
      paused: "In pausa: questo tempo non viene fatturato.",
      pauseFailed: "Impossibile mettere in pausa o riprendere il timer.",
    },
    statement: {
      title: "Genera estratto conto",
      description: "Crea un estratto conto per un cliente e un periodo di fatturazione.",
      customer: "Cliente",
      noBilledProjects: "Questo cliente non ha ancora progetti fatturati",
      cycle: "Ciclo di fatturazione",
      cycleWeekly: "Settimanale",
      cycleBiweekly: "Ogni due settimane",
      periodStart: "Inizio periodo",
      periodEnd: "Fine periodo",
      periodNote: "Sono inclusi solo il tempo fatturato e non bloccato di questo periodo. Le voci generate diventano di sola lettura.",
      generate: "Genera estratto conto",
      working: "Elaborazione in corso…",
      created: "Estratto conto {{name}} creato",
      number: "Numero dell’estratto conto",
      total: "Totale",
      entries: "Voci",
      downloadPdf: "Scarica PDF",
      periodFailed: "Impossibile caricare il periodo suggerito",
      cycleFailed: "Impossibile modificare il ciclo di fatturazione",
      generateFailed: "Impossibile generare l’estratto conto",
      downloadFailed: "Impossibile scaricare il PDF",
    },
    rates: {
      title: "Tariffe del progetto",
      unchanged: "Lascia vuoto per mantenere",
      useDefault: "Usa il valore predefinito dell’azienda",
      help: "Le nuove voci usano la tariffa in vigore. Le voci esistenti mantengono la tariffa con cui sono state create.",
      companyDefault: "predefinito dell’azienda",
      saved: "Salvato. Sviluppatore: {{developer}} · Risorsa: {{resource}}",
      saveFailed: "Impossibile salvare le tariffe.",
    },
    settings: {
      title: "Tempo e fatturazione",
      description: "Tariffe orarie predefinite e coordinate bancarie stampate sugli estratti conto dei clienti.",
      developerRate: "Tariffa Sviluppatore ($/h)",
      resourceRate: "Tariffa Risorsa ($/h)",
      ratesHelp: "Un progetto può sostituire una delle due tariffe. Modificare una tariffa non cambia le voci esistenti.",
      achTitle: "Dettagli di pagamento ACH",
      achComplete: "Completo",
      achIncomplete: "Incompleto — gli estratti conto indicheranno che ACH non è configurato",
      bankName: "Nome della banca",
      accountName: "Intestatario del conto",
      routingNumber: "Numero di instradamento",
      accountNumber: "Numero di conto",
      endingIn: "che termina con {{last4}}",
      notSet: "non impostato",
      replacePlaceholder: "Digita un nuovo numero per sostituirlo",
      achHelp: "Memorizzato in forma cifrata. Qui sono mostrate solo le ultime 4 cifre; i numeri completi compaiono solo negli estratti conto.",
      nothingChanged: "Niente da salvare.",
      saved: "Salvato.",
      saveFailed: "Impossibile salvare le impostazioni.",
      loadFailed: "Impossibile caricare le impostazioni.",
    },
  },
};
